import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense } from 'react';
import { useFrame } from '@react-three/fiber';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ViewerCanvas from './components/ViewerCanvas';
import ModelInfoPanel from './components/ModelInfoPanel';
import SellerListModal from '../model-catalog/components/SellerListModal';
import LightingControls from './components/LightingControls';
import AnnotationPanel from './components/AnnotationPanel';
import MeasurementTools from './components/MeasurementTools';
import ViewerControls from './components/ViewerControls';
import MobileViewerHeader from './components/MobileViewerHeader';
import MobileViewerControls from './components/MobileViewerControls';
import MobilePartsDrawer from './components/MobilePartsDrawer';
import MobileSpecsDrawer from './components/MobileSpecsDrawer';
import PartRequestModal from './components/PartRequestModal';
import SellerOfferingDetailModal from '../model-catalog/components/SellerOfferingDetailModal';
import Button from '../../components/ui/Button';
import { productService } from '../../services/productService';
import { componentService } from '../../services/componentService';
import { cartService } from '../../services/cartService';
import { annotationService } from '../../services/annotationService';
import { useAuth } from '../../contexts/AuthContext';
import { repairGuideService } from '../../services/repairGuideService';
import RepairGuidePanel from './components/RepairGuidePanel';
import RepairGuideBuilderPanel from './components/RepairGuideBuilderPanel';
import { userService } from '../../services/userService';
import { supabase } from '../../lib/supabase';
import LoginSignupModal from '../../components/ui/LoginSignupModal';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Environment } from '@react-three/drei';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import * as THREE from 'three';
import { formatPrice } from '../../utils/formatters';
import Icon from '../../components/ui/Icon';

const sanitizeImageUrl = (url) => {
  if (!url) return '';
  // If it is a hardcoded localhost URL from local development db records,
  // convert it to a dynamic/relative path so it works flawlessly in production!
  if (url.includes('localhost:3000') || url.includes('localhost:3001') || url.includes('localhost:5173')) {
    const parts = url.split('/assets/');
    if (parts.length > 1) {
      return '/assets/' + parts[1];
    }
  }
  return url;
};

const XOR_KEY = 0x4F; // Symmetrical XOR decryption Key to unlock scrambled files

function deobfuscateBuffer(arrayBuffer) {
  const view = new Uint8Array(arrayBuffer);
  const limit = Math.min(view.length, 100);
  for (let i = 0; i < limit; i++) {
    view[i] = view[i] ^ XOR_KEY;
  }
  return arrayBuffer;
}

// Symmetrical 3D Mini-Mesh Component Viewer:
// Resolves, auto-centers, and fits exactly one tiny, lightweight .glb chunk in isolation.
// Fetches the S3 file as a raw binary ArrayBuffer, bypassing three.js URL extension parsers
// to prevent any query parameter authentication token mismatches and guarantee 100% stable loads!
// Features automatic continuous 360 rotation unless mouse is hovering over or active interactions are triggered.
const MiniMeshViewer = ({ chunkUrl, partName }) => {
  const [scene, setScene] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const meshRef = useRef();

  // R3F frame tick to continuously rotate the isolated part
  useFrame((state, delta) => {
    if (meshRef.current && !isHovered) {
      meshRef.current.rotation.y += delta * 0.45; // 360 continuous rotation
    }
  });

  useEffect(() => {
    if (!chunkUrl) return;

    const loadMesh = async () => {
      try {
        const response = await fetch(chunkUrl);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const arrayBuffer = await response.arrayBuffer();
        const deobfuscated = deobfuscateBuffer(arrayBuffer);

        const loader = new GLTFLoader();
        loader.parse(
          deobfuscated,
          '',
          (gltf) => {
            const loadedScene = gltf.scene;

            // Symmetrical Monolithic Part Isolation:
            // If partName is provided, we clone and isolate just this one single part mesh cleanly!
            if (partName) {
              const cleanPartName = partName.toLowerCase().replace(/[^a-z0-9]/g, '');
              let matchedMesh = null;
              
              // First traverse to find exact or fuzzy match
              loadedScene.traverse((node) => {
                if (node.isMesh) {
                  const nodeNameClean = node.name.toLowerCase().replace(/[^a-z0-9]/g, '');
                  if (nodeNameClean === cleanPartName || nodeNameClean.includes(cleanPartName) || cleanPartName.includes(nodeNameClean)) {
                    matchedMesh = node.clone(); // Clone the matched mesh!
                  }
                }
              });
              
              if (matchedMesh) {
                // Symmetrical center of mass vertices translation for isolated cloned mesh
                if (matchedMesh.geometry) {
                  matchedMesh.geometry.computeBoundingBox();
                  const localBox = matchedMesh.geometry.boundingBox;
                  const center = new THREE.Vector3();
                  localBox.getCenter(center);
                  matchedMesh.geometry.translate(-center.x, -center.y, -center.z);
                  matchedMesh.position.set(0, 0, 0);
                }
                const singleGroup = new THREE.Group();
                singleGroup.add(matchedMesh);
                setScene(singleGroup);
                return;
              }
            }

            // Mass-Centering Vertices for fallback full scene:
            // Traverse all mesh geometries inside the scene and shift their vertices
            // natively to [0,0,0], perfectly resetting the mesh's center of mass!
            loadedScene.traverse((node) => {
              if (node.isMesh && node.visible && node.geometry) {
                node.geometry.center(); // Center geometry buffer vertices natively!
                node.position.set(0, 0, 0); // Reset local parent coordinate offsets completely!
                node.rotation.set(0, 0, 0); // Reset local parent rotation offsets completely!
              }
            });

            // Compute the new uniform bounding box of the auto-centered meshes
            const box = new THREE.Box3().setFromObject(loadedScene);
            const size = box.getSize(new THREE.Vector3());

            // Normalize scaling uniformly based on the maximum dimension
            const maxDim = Math.max(size.x, size.y, size.z);
            const scaleFactor = maxDim > 0 ? (1.5 / maxDim) : 1;
            loadedScene.scale.setScalar(scaleFactor);
            loadedScene.position.set(0, 0, 0); // Seat exactly at coordinate origin!

            setScene(loadedScene);
          },
          (err) => {
            console.error('Error parsing GLB buffer:', err);
          }
        );
      } catch (err) {
        console.error('Error fetching GLB chunk:', err);
      }
    };

    loadMesh();
  }, [chunkUrl]);

  if (!scene) return null;

  return (
    <primitive 
      ref={meshRef} 
      object={scene} 
      position={[0, 0, 0]} 
      onPointerOver={(e) => { e.stopPropagation(); setIsHovered(true); }}
      onPointerOut={(e) => { e.stopPropagation(); setIsHovered(false); }}
      onPointerDown={(e) => { e.stopPropagation(); setIsHovered(true); }}
    />
  );
};

const ModelViewer = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const { sku } = params;
  const seoCategory = window.location.pathname.split('/')[1];
  
  // Splat param comes from /:seoCategory/* route
  const splatPath = params['*']; 
  
  let derivedSlug = null;
  let derivedIntent = 'parts-diagram';
  let reconstructedName = '';
  
  if (splatPath) {
     const pathSegments = splatPath.split('/').filter(Boolean);
     const lastSegment = pathSegments[pathSegments.length - 1];
     if (['parts-diagram', 'brake-system-diagram', 'engine-components'].includes(lastSegment) || /^\d+-parts-list$/.test(lastSegment)) {
         derivedIntent = pathSegments.pop();
     }
     derivedSlug = pathSegments.join('-');
     reconstructedName = pathSegments.join(' ');
  }

  const modelId = searchParams.get('id');
  const initialComponentId = searchParams.get('componentId');
  
  // Update initialComponentName logic to also use derivedPartSlug if we had one
  const initialComponentName = searchParams.get('componentName');
  const { user } = useAuth();

  const [isPartRequestModalOpen, setIsPartRequestModalOpen] = useState(false);
  const [requestPartName, setRequestPartName] = useState('');

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [watermarkSettings, setWatermarkSettings] = useState({ enabled: true, imageUrl: '/assets/images/OpenGadget-SMALL-GREY-TRANS-1763756659192.png' });

  const canvasRef = useRef(null);
  const loadedProductIdRef = useRef(null); // Ref to track loaded product ID to prevent router replacement infinite loops!
  const [isMobile, setIsMobile] = useState(false);
  const [showMobilePartsDrawer, setShowMobilePartsDrawer] = useState(false);
  const [showMobileSpecs, setShowMobileSpecs] = useState(false);
  const [mobileSearchTerm, setMobileSearchTerm] = useState('');
  const [isInfiniteVertical, setIsInfiniteVertical] = useState(false);

  useEffect(() => {
    const checkMobile = async () => {
      try {
        const { UAParser } = await import('ua-parser-js');
        const parser = new UAParser();
        const result = parser.getResult();
        const isMobileDevice = result.device.type === 'mobile' || result.browser.name?.includes('Mobile');
        const isSmallScreen = window.innerWidth <= 768;
        setIsMobile(isMobileDevice || isSmallScreen);
      } catch (err) {
        setIsMobile(window.innerWidth <= 768);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const [product, setProduct] = useState(null);
  const [databaseComponents, setDatabaseComponents] = useState([]);
  const [extractedComponents, setExtractedComponents] = useState([]);
  const [componentVisibility, setComponentVisibility] = useState({});
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [isMultiSelectActive, setIsMultiSelectActive] = useState(false);
  const [selectedComponentIds, setSelectedComponentIds] = useState([]);
  const [gizmoMode, setGizmoMode] = useState('translate'); // 'translate' | 'rotate' | 'scale'
  const [selectedOffering, setSelectedOffering] = useState(null);
  const [selectedComponentOffering, _setSelectedComponentOffering] = useState(null);
  const setSelectedComponentOffering = useCallback((offering) => {
    if (offering?.isSourcingRequest) {
      if (product?.sell_product_only || product?.sellProductOnly) {
        setNotification({
          show: true,
          message: `This product is configured for "Sell Complete Assembly Only". Individual components are not available for separate sourcing or purchasing.`,
          type: 'error'
        });
        setTimeout(() => setNotification({ show: false, message: '', type: '' }), 5000);
        return;
      }
    }
    _setSelectedComponentOffering(offering);
  }, [product]);
  const [doubleClickedSellers, setDoubleClickedSellers] = useState([]);
  const [isOfferingModalOpen, setIsOfferingModalOpen] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [isAdded, setIsAdded] = useState(false);
  const [componentColors, setComponentColors] = useState({});
  const [annotations, setAnnotations] = useState([]);

  // Symmetrical High-Performance Top-Center Loading HUD
  const [chunkProgress, setChunkProgress] = useState(0);
  const [hudState, setHudState] = useState('hidden'); // 'loading' | 'complete' | 'hidden'

  const parentIsChunked = !!product?.is_chunked || !!product?.isChunked || product?.modelUrl?.includes('manifest') || product?.modelUrl?.includes('.json');

  // Symmetrical HUD Status Sync inside parent page context:
  // Monitors model compilation progress to toggle the premium Top-Center loading/complete banners!
  useEffect(() => {
    if (parentIsChunked) {
      if (chunkProgress < 100) {
        setHudState('loading');
      } else if (chunkProgress === 100) {
        setHudState('complete');
      }
    } else {
      setHudState('hidden');
    }
  }, [chunkProgress, parentIsChunked]);

  // Symmetrical HUD Fadeout Timer:
  // Triggered only when hudState changes to 'complete', transitioning to 'fadeout' after 4 seconds
  // (triggering a beautiful 1-second CSS opacity transition) and unmounting ('hidden') at 5 seconds!
  useEffect(() => {
    if (hudState === 'complete') {
      const fadeTimer = setTimeout(() => {
        setHudState('fadeout');
      }, 4000);

      const hideTimer = setTimeout(() => {
        setHudState('hidden');
      }, 5000);

      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(hideTimer);
      };
    }
  }, [hudState]);

  // Symmetrical Model Reset:
  // Reset loading progress state to 0 and hide HUD immediately on model URL changes!
  useEffect(() => {
    setChunkProgress(0);
    setHudState('hidden');
  }, [product?.modelUrl]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(searchParams.get('isPlaying') !== 'false');
  const [showGrid, setShowGrid] = useState(false);
  const [showModelInfo, setShowModelInfo] = useState(true);
  const [showLighting, setShowLighting] = useState(false);
  const [showAnnotations, setShowAnnotations] = useState(false);
  const [showMeasurements, setShowMeasurements] = useState(false);
  const [productSellers, setProductSellers] = useState([]);
  const [zRayComponentIds, setZRayComponentIds] = useState([]);
  
  // Custom right-click Context Menu state for WebGL components:
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, component: null });

  // Symmetrical Click-Away Listener: Dismisses the component right-click context menu when clicking anywhere on the screen!
  useEffect(() => {
    if (!contextMenu.show) return;

    const handleDismissContextMenu = () => {
      setContextMenu({ show: false, x: 0, y: 0, component: null });
    };

    const timer = setTimeout(() => {
      window.addEventListener('click', handleDismissContextMenu);
      window.addEventListener('contextmenu', handleDismissContextMenu);
    }, 50);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('click', handleDismissContextMenu);
      window.removeEventListener('contextmenu', handleDismissContextMenu);
    };
  }, [contextMenu.show]);
  const [showSellersModal, setShowSellersModal] = useState(false);
  const [measurements, setMeasurements] = useState([]);
  const [activeMeasurementTool, setActiveMeasurementTool] = useState(null);
  const [selectedMeasurementId, setSelectedMeasurementId] = useState(null);
  const [unit, setUnit] = useState('mm');

  // Symmetrical B2B Repair Shop CRM Diagnostics States:
  const mode = searchParams.get('mode'); // 'repair' | 'wrap'
  const assemblyId = searchParams.get('assemblyId');
  const configId = searchParams.get('configId');
  const shopId = searchParams.get('shopId');
  
  const [repairLogs, setRepairLogs] = useState([]);
  const [showLogRepairModal, setShowLogRepairModal] = useState(false);
  const [selectedAnnotationDetail, setSelectedAnnotationDetail] = useState(null); // Tracks the currently double-clicked annotation for detailed modal viewing!
  const [isAnnotationDetailModalOpen, setIsAnnotationDetailModalOpen] = useState(false); // Tracks whether the detailed annotation modal is open!
  const [isRepairModalOpen, setIsRepairModalOpen] = useState(false); // Tracks whether the Coming Soon repair modal is open!
  
  // Enterprise Repair Guides state declarations:
  const [repairGuides, setRepairGuides] = useState([]);
  const [activeGuideId, setActiveGuideId] = useState('');
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [repairBuilderActive, setRepairBuilderActive] = useState(false);
  const [repairGuideOffsets, setRepairGuideOffsets] = useState({});
  const [activeStepActions, setActiveStepActions] = useState([]);
  const [repairGizmoMode, setRepairGizmoMode] = useState('translate'); // 'translate' or 'rotate'
  const [showStringLines, setShowStringLines] = useState(false); // Sticky viewer-level toggle for dashed connector lines

  const loadRepairGuides = async () => {
    try {
      const data = await repairGuideService.getByProductOrComponent(modelId, initialComponentId);
      setRepairGuides(data);
      if (data.length > 0 && !activeGuideId) {
        setActiveGuideId(data[0].id);
      }
    } catch (err) {
      console.error('Error loading repair guides:', err);
    }
  };

  useEffect(() => {
    if (modelId || initialComponentId) {
      loadRepairGuides();
    }
  }, [modelId, initialComponentId]);

  // Compute cumulative step offsets for the currently selected guide in viewer mode
  useEffect(() => {
    if (repairBuilderActive) return; // In builder mode, RepairGuideBuilderPanel dictates cumulative offsets

    const activeGuide = repairGuides.find(g => g.id === activeGuideId);
    if (!activeGuide || !activeGuide.steps || activeGuide.steps.length === 0) {
      setRepairGuideOffsets({});
      return;
    }

    const cumulative = {};
    for (let i = 0; i <= activeStepIndex; i++) {
      const step = activeGuide.steps[i];
      if (step && step.actions) {
        step.actions.forEach(action => {
          cumulative[action.partName] = {
            x: action.targetX || 0,
            y: action.targetY || 0,
            z: action.targetZ || 0,
            rx: action.targetRx || 0,
            ry: action.targetRy || 0,
            rz: action.targetRz || 0,
            isRemoved: action.isRemoved || false
          };
        });
      }
    }
    setRepairGuideOffsets(cumulative);
  }, [activeStepIndex, activeGuideId, repairGuides, repairBuilderActive]);

  const activeGuide = repairGuides.find(g => g.id === activeGuideId);
  const activeStep = activeGuide?.steps?.[activeStepIndex];
  const activeStepCamera = activeStep && activeStep.cameraAngles && activeStep.cameraAngles.length > 0 ? {
    position: activeStep.cameraAngles[0].position,
    target: activeStep.cameraAngles[0].target
  } : null;

  const handleRepairPartMoved = (partName, dx, dy, dz, drx, dry, drz) => {
    window.dispatchEvent(new CustomEvent('repair-part-moved', {
      detail: { partName, dx, dy, dz, drx, dry, drz }
    }));
  };

  const handleStepSelect = (idx) => {
    setActiveStepIndex(idx);
    
    // Smoothly trigger viewport glide to the first camera viewpoint of this step if defined!
    const activeGuideObj = repairGuides.find(g => g.id === activeGuideId);
    const step = activeGuideObj?.steps?.[idx];
    if (step && step.cameraAngles && step.cameraAngles.length > 0) {
      // Trigger a 3D camera preview glide to the first viewpoint of this step!
      window.dispatchEvent(new CustomEvent('apply-step-camera', { detail: step.cameraAngles[0] }));
    }
  };

  const handleCloseRepairPanel = () => {
    setIsRepairModalOpen(false);
    setRepairBuilderActive(false);
    setRepairGuideOffsets({});
    setActiveStepIndex(0);
    setActiveStepActions([]);
    setShowStringLines(false);
  };

  const [repairForm, setRepairForm] = useState({ status: 'needs_repair', cost: '0.00', notes: '' });
  const [componentWraps, setComponentWraps] = useState({});
  const [componentWrapsBefore, setComponentWrapsBefore] = useState({});
  const [showBeforeWrap, setShowBeforeWrap] = useState(false);
  const [showWrapSuccessModal, setShowWrapSuccessModal] = useState(false);
  const [savedProjectName, setSavedProjectName] = useState('');
  const [wrapName, setWrapName] = useState('Project Design');
  const [wrapNotes, setWrapNotes] = useState('');
  const [gizmoSize, setGizmoSize] = useState(0.8); // Symmetrical: Tracks the custom 3D Transform Gizmo handle size!

  // Load Custom Configuration on Mount (Wraps or Swaps!)
  useEffect(() => {
    const fetchWrapConfig = async () => {
      if (configId) {
        const table = mode === 'swap' ? 'seller_swap_configs' : 'custom_wrap_configs';
        const { data } = await supabase
          .from(table)
          .select('*')
          .eq('id', configId)
          .maybeSingle();
        if (data) {
          if (data.config_data) {
            const config = data.config_data;
            if (config.before || config.after) {
              setComponentWraps(config.after || {});
              setComponentWrapsBefore(config.before || {});
              setWrapNotes(config.notes || '');
            } else {
              setComponentWraps(config);
              setComponentWrapsBefore({});
              setWrapNotes('');
            }
          }
          if (data.config_name) setWrapName(data.config_name);
        }
      }
    };
    fetchWrapConfig();
  }, [configId, mode]);

  // Fetch Service Logs on Mount/Update
  useEffect(() => {
    const fetchRepairLogs = async () => {
      if (mode === 'repair' && assemblyId) {
        const { data } = await supabase
          .from('assembly_service_records')
          .select('*')
          .eq('assembly_id', assemblyId);
        if (data) setRepairLogs(data);
      }
    };
    fetchRepairLogs();
  }, [mode, assemblyId]);

  // Map service record array to format compatible with ViewerCanvas for mesh highlights!
  const diagnosticMap = useMemo(() => {
    const map = {};
    repairLogs.forEach(r => {
      if (r.component_id) {
        map[r.component_id] = r.status;
      }
    });
    return map;
  }, [repairLogs]);

  // Increment shop and seller product views on load
  useEffect(() => {
    const incrementShopViewsOnLoad = async () => {
      // Symmetrical validation: Prevent database syntax exceptions if shopId is 'undefined' or malformed!
      if (!shopId || shopId === 'undefined' || shopId.length !== 36 || !shopId.includes('-')) return;
      try {
        // Resolve seller account by trying both id and user_id fields
        let { data: sellerAcc } = await supabase
          .from('seller_accounts')
          .select('id, user_id')
          .eq('id', shopId)
          .maybeSingle();

        if (!sellerAcc) {
          const { data: sellerAccByUid } = await supabase
            .from('seller_accounts')
            .select('id, user_id')
            .eq('user_id', shopId)
            .maybeSingle();
          sellerAcc = sellerAccByUid;
        }

        if (sellerAcc?.id) {
          const shopType = mode === 'repair' ? 'repair' : mode === 'wrap' ? 'wrap' : mode === 'swap' ? 'swap' : 'storefront';
          supabase?.rpc('increment_shop_views', { target_shop_id: sellerAcc.id, shop_type: shopType })?.then(undefined, err => {
            console.error('Error auto-incrementing shop views:', err);
          });

          // Symmetrical Seller Product View Count Increment:
          // If a specific product (modelId) is loaded under this seller's storefront context,
          // resolve the seller product ID and increment its view count!
          if (modelId) {
            const { data: sellerProd } = await supabase
              .from('seller_products')
              .select('id')
              .eq('product_id', modelId)
              .eq('seller_id', sellerAcc.id)
              .eq('status', 'active')
              .maybeSingle();

            if (sellerProd?.id) {
              supabase?.rpc('increment_seller_product_views', { seller_product_id: sellerProd.id })?.then(undefined, err => {
                console.error('Error auto-incrementing seller product views on load:', err);
              });
            }
          }

          // Symmetrical Seller Component View Count Increment:
          // If a specific component (initialComponentId) is loaded under this seller's storefront context,
          // resolve the seller component ID and increment its view count!
          if (initialComponentId && typeof initialComponentId === 'string') {
            // Check if initialComponentId matches UUID pattern
            const isUuid = initialComponentId.length === 36 && initialComponentId.includes('-');
            if (isUuid) {
              const { data: sellerComp } = await supabase
                .from('seller_components')
                .select('id')
                .eq('seller_id', sellerAcc.id)
                .or(`id.eq.${initialComponentId},component_id.eq.${initialComponentId}`)
                .eq('status', 'active')
                .maybeSingle();

              if (sellerComp?.id) {
                supabase?.rpc('increment_seller_component_views', { seller_component_id: sellerComp.id })?.then(undefined, err => {
                  console.error('Error auto-incrementing seller component views on load:', err);
                });
              }
            }
          }
        }
      } catch (err) {
        console.error('Error in incrementShopViewsOnLoad:', err);
      }
    };
    incrementShopViewsOnLoad();
  }, [shopId, modelId, initialComponentId, mode]);

  // Increment seller product views when selectedOffering changes (indicating it is being viewed)
  useEffect(() => {
    if (selectedOffering && selectedOffering.id) {
      supabase?.rpc('increment_seller_product_views', { seller_product_id: selectedOffering.id })?.then(undefined, err => {
        console.error('Error auto-incrementing seller product views:', err);
      });
    }
  }, [selectedOffering?.id]);

  // Increment seller component views when selectedComponentOffering is viewed
  useEffect(() => {
    const isUUID = (str) => typeof str === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(str);
    if (selectedComponentOffering && selectedComponentOffering.id && isUUID(selectedComponentOffering.id)) {
      supabase?.rpc('increment_seller_component_views', { seller_component_id: selectedComponentOffering.id })?.then(undefined, err => {
        console.error('Error auto-incrementing seller component views:', err);
      });
    }
  }, [selectedComponentOffering?.id]);

  // Symmetrical Store Custom AFTERMARKET Swaps Loader:
  // Loads all custom swap packages published by this specific shop for the currently viewed vehicle!
  const [storeSwapConfigs, setStoreSwapConfigs] = useState([]);
  useEffect(() => {
    const loadStoreSwapConfigs = async () => {
      if (modelId && shopId) {
        try {
          const { data } = await supabase
            .from('seller_swap_configs')
            .select('*')
            .eq('product_id', modelId)
            .eq('shop_id', shopId)
            .order('created_at', { ascending: false });
          if (data) {
            setStoreSwapConfigs(data);
          }
        } catch (e) {
          console.error('Failed to load store swap configurations:', e);
        }
      }
    };
    loadStoreSwapConfigs();
  }, [modelId, shopId]);

  // Symmetrical Seller's Component Inventory Loader (For dropdown matching inside configurator edit mode)
  const [sellerComponents, setSellerComponents] = useState([]);
  useEffect(() => {
    const loadSellerComponents = async () => {
      const targetUserId = shopId || user?.id; // Symmetrical: Fallback dynamically to shopId (store owner) or active user!
      if (mode === 'swap' && targetUserId) {
        try {
          const { data: sellerAcc } = await supabase
            .from('seller_accounts')
            .select('id')
            .eq('user_id', targetUserId)
            .maybeSingle();
            
          if (sellerAcc) {
            const { data: comps } = await supabase
              .from('seller_components')
              .select(`
                id, custom_name,
                component:components (
                  id, name, stl_file_path
                )
              `)
              .eq('seller_id', sellerAcc.id)
              .or('isdeleted.is.null,isdeleted.eq.false');
              
            if (comps) {
              setSellerComponents(comps.map(c => ({
                id: c.id,
                name: c.custom_name || c.component?.name || 'Unnamed Part',
                glbUrl: c.component?.stl_file_path || c.image_url || '',
                isAccessory: c.is_accessory || c.component?.is_accessory || false // Symmetrical: Map custom accessory flag natively!
              })).filter(c => c.glbUrl));
            }
          }
        } catch (e) {
          console.error('Failed to load seller components for swaps:', e);
        }
      }
    };
    loadSellerComponents();
  }, [mode, user, shopId]);

  // Reset active measurement tool when measurements panel is closed
  useEffect(() => {
    if (!showMeasurements) {
      setActiveMeasurementTool(null);
    }
  }, [showMeasurements]);

  const [viewMode, setViewMode] = useState(
    searchParams.get('viewMode') || (searchParams.get('componentId') ? 'x-ray-gray' : 'shaded')
  );

  // Sync viewMode with URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (viewMode !== 'shaded') {
      params.set('viewMode', viewMode);
    } else {
      params.delete('viewMode');
    }
    const newRelativePathQuery = window.location.pathname + '?' + params.toString();
    window.history.replaceState(null, '', newRelativePathQuery);
  }, [viewMode]);

  const [isExploded, setIsExploded] = useState(false);
  const [explodeScaleX, setExplodeScaleX] = useState(1);
  const [explodeScaleY, setExplodeScaleY] = useState(1);
  const [explodeScaleZ, setExplodeScaleZ] = useState(1);
  const [isExplodeLocked, setIsExplodeLocked] = useState(true);

  const handleScaleChangeX = (val) => {
    setExplodeScaleX(val);
    if (isExplodeLocked) {
      setExplodeScaleY(val);
      setExplodeScaleZ(val);
    }
  };
  const handleScaleChangeY = (val) => {
    setExplodeScaleY(val);
    if (isExplodeLocked) {
      setExplodeScaleX(val);
      setExplodeScaleZ(val);
    }
  };
  const handleScaleChangeZ = (val) => {
    setExplodeScaleZ(val);
    if (isExplodeLocked) {
      setExplodeScaleX(val);
      setExplodeScaleY(val);
    }
  };

  const handleToggleExplodeLock = () => {
    setIsExplodeLocked(prev => {
      const next = !prev;
      if (next) {
        setExplodeScaleY(explodeScaleX);
        setExplodeScaleZ(explodeScaleX);
      }
      return next;
    });
  };

  const handleAddParentProductToCart = async () => {
    try {
      if (!user) {
        setShowAuthModal(true);
        return;
      }

      // If there are multiple sellers available, show the Sellers modal instead of adding a single seller!
      if (productSellers.length > 1) {
        setShowSellersModal(true);
        return;
      }
      
      // Query the active seller offering for this parent product with seller accounts joined
      const { data: sellerProds } = await supabase
        .from('seller_products')
        .select(`
          id, 
          price, 
          seller_id,
          is_self_manufacturing,
          seller_accounts (
            id,
            business_name,
            business_slug,
            rating
          )
        `)
        .eq('product_id', product.id)
        .ilike('status', 'active')
        .gt('price', 0)
        .or('isdeleted.is.null,isdeleted.eq.false')
        .limit(1);

      const sellerProd = sellerProds?.[0];

      if (!sellerProd) {
        throw new Error('This product is not currently available for purchase from any active seller.');
      }

      // Check if the parent product listing has 3D/CNC manufacturing capabilities active
      const is3D = product.is3dManufacturing || product.isCncCapable;

      if (is3D) {
          // Query custom override manufacturing_options for this seller product if they exist
          const { data: mfgOpt } = await supabase
            .from('manufacturing_options')
            .select('*')
            .eq('seller_product_id', sellerProd.id)
            .maybeSingle();

          const fullProductOffering = {
            id: sellerProd.id, // seller_products.id
            productId: product.id,
            name: product.name,
            sku: product.sku,
            thumbnailPath: product.thumbnailPath,
            is3dManufacturing: product.is3dManufacturing,
            isCncCapable: product.isCncCapable,
            isSelfManufacturing: sellerProd.is_self_manufacturing !== false,
            sellerBusinessName: sellerProd.seller_accounts?.business_name,
            seller: sellerProd.seller_accounts,
            mfg3dTypes: mfgOpt ? (mfgOpt.mfg_3d_types || []) : (product.mfg3dTypes || []),
            mfg3dMaterials: mfgOpt ? (mfgOpt.mfg_3d_materials || []) : (product.mfg3dMaterials || []),
            mfg3dColors: mfgOpt ? (mfgOpt.mfg_3d_colors || {}) : (product.mfg3dColors || {}),
            mfgCncTypes: mfgOpt ? (mfgOpt.mfg_cnc_types || []) : (product.mfgCncTypes || []),
            mfgCncMaterials: mfgOpt ? (mfgOpt.mfg_cnc_materials || []) : (product.mfgCncMaterials || []),
            mfgCncColors: mfgOpt ? (mfgOpt.mfg_cnc_colors || {}) : (product.mfgCncColors || {}),
            globalItem: product,
            price: sellerProd.price
          };

          setSelectedOffering(fullProductOffering);
          setIsProductModalOpen(true);
          return;
      }

      const payload = {
        userId: user.id,
        productId: product.id,
        sellerProductId: sellerProd.id,
        sellerId: sellerProd.seller_id,
        quantity: 1,
        price: sellerProd.price,
        metadata: { type: 'physical' }
      };
      await cartService.addToCart(payload);
      setNotification({ show: true, message: `Added entire product "${product.name}" to cart!`, type: 'success' });
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 4000);
    } catch (err) {
      console.error('Failed to add parent product to cart:', err);
      setNotification({ show: true, message: err.message || 'Failed to add product to cart', type: 'error' });
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 4000);
    }
  };

  const [lighting, setLighting] = useState({
    intensity: 75,
    temperature: 5500,
    shadowSoftness: 50,
    environment: 30,
    ambientOcclusion: 40,
    preset: 'studio',
    showEnvironmentBackground: false,
  });

  const canonicalUrl = useMemo(() => {
    if (sku) return `${window.location.origin}/part/${sku}`;
    if (derivedSlug && seoCategory) {
      return `${window.location.origin}/3d-model-viewer/${seoCategory}/${splatPath}${initialComponentId ? `?componentId=${initialComponentId}` : ''}`;
    }
    return `${window.location.origin}/3d-model-viewer?id=${modelId || product?.id}${product?.name ? `&name=${encodeURIComponent(product.name)}` : ''}${product?.componentCount ? `&part_count=${product.componentCount}` : ''}`;
  }, [modelId, product, sku, derivedSlug, splatPath, seoCategory, initialComponentId]);

  // Data Loading
  useEffect(() => {
    const loadAllData = async () => {
      // Symmetrical Load Prevention: Bypasses fetches and loading flickers if the correct product is already loaded!
      if (product) {
        const prodId = (product.id || '').toLowerCase();
        const mId = (modelId || '').toLowerCase();
        const prodSlug = (product.slug || product.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
        const dSlug = (derivedSlug || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');

        if (mId && prodId === mId) return;
        if (dSlug && prodSlug === dSlug) return;
        
        // Symmetrical reset: Clear out-of-date product state to prevent visual carry-over or double-loading of old models!
        setProduct(null);
      }
      try {
        setLoading(true);
        setError('');
        let productData = null;
        
        if (modelId) {
            productData = await productService.getById(modelId);
        } else if (initialComponentId) {
            const comp = await componentService.getById(initialComponentId).catch(() => null);
            if (comp && comp.productId) {
                productData = await productService.getById(comp.productId).catch(() => null);
            }
        }
        
        if (!productData && sku) {
            const { products } = await productService.getPublicCatalogProducts({ searchTerm: sku, itemsPerPage: 1 });
            productData = products?.[0];
            if (!productData) {
                const comps = await componentService.getAll({ searchTerm: sku });
                if (comps?.[0]?.productId) productData = await productService.getById(comps[0].productId).catch(() => null);
            }
        } 
        
        if (!productData && derivedSlug) {
            productData = await productService.getBySlug(derivedSlug).catch(e => null);
            if (!productData && reconstructedName) {
                const { products } = await productService.getPublicCatalogProducts({ searchTerm: reconstructedName, itemsPerPage: 1 });
                productData = products?.[0];
            }
        }

        // NEW: If still no product, pick a random one from Featured/Staff Pick/Certified
        if (!productData && !modelId && !sku && !derivedSlug) {
          try {
            // First try Featured/Staff/Certified
            const { data: featuredModels } = await supabase
              .from('products')
              .select('id, name, gltf_file_path')
              .or('is_featured.eq.true,staffpick.eq.true,is_certified.eq.true')
              .eq('status', 'active')
              .not('gltf_file_path', 'is', null) // Ensure it has a model
              .limit(10);
            
            let targetId = null;
            if (featuredModels && featuredModels.length > 0) {
              targetId = featuredModels[Math.floor(Math.random() * featuredModels.length)].id;
            } else {
              // Fallback to ANY active model with a file
              const { data: allModels } = await supabase
                .from('products')
                .select('id, name, gltf_file_path')
                .eq('status', 'active')
                .not('gltf_file_path', 'is', null)
                .limit(20);
              if (allModels && allModels.length > 0) {
                targetId = allModels[Math.floor(Math.random() * allModels.length)].id;
              }
            }

            if (targetId) {
              productData = await productService.getById(targetId);
              if (productData && productData.modelUrl) {
                // Update URL without reloading to reflect the random selection
                const newParams = new URLSearchParams(window.location.search);
                newParams.set('id', targetId);
                window.history.replaceState(null, '', `${window.location.pathname}?${newParams.toString()}`);
              } else {
                productData = null; // Incomplete data, don't use
              }
            }
          } catch (randomErr) {
            console.error('Error fetching random model:', randomErr);
          }
        }

        if (!productData) {
          setError(`Select a model from the catalog to begin visualization.`);
          setLoading(false);
          return;
        }

        if (loadedProductIdRef.current === productData.id) {
          // Already loaded this product! Bypasses state settings to prevent loop-reloads on router replacement!
          setLoading(false);
          return;
        }
        loadedProductIdRef.current = productData.id;

        // Symmetrical Relative URL Resolver:
        // Forcefully resolve relative Supabase Storage paths into fully-qualified public CDN endpoints!
        if (productData.modelUrl && !productData.modelUrl.startsWith('http')) {
          const { data: storageUrlData } = supabase.storage.from('product-3d-models').getPublicUrl(productData.modelUrl);
          productData.modelUrl = storageUrlData?.publicUrl || productData.modelUrl;
        }

        // Symmetrical S3/CDN Cache-Busting Pipeline:
        // Appends the product's actual database updated_at timestamp as a query parameter,
        // forcing the browser and S3 storage proxies to bypass stale memory and load updated models instantly!
        if (productData.modelUrl) {
          const version = productData.updatedAt 
            ? new Date(productData.updatedAt).getTime() 
            : (productData.updated_at ? new Date(productData.updated_at).getTime() : 1);
          
          const separator = productData.modelUrl.includes('?') ? '&' : '?';
          productData.modelUrl = `${productData.modelUrl}${separator}v=${version}`;
        }

        setProduct(productData);
        
        let dbComps = [];
        try {
          dbComps = await componentService.getByProductId(productData.id);
        } catch (e) {
          console.warn('Error loading seller components, falling back to global components:', e);
        }

        // Always fetch the complete global components hierarchy for this product to ensure the full parts list is available instantly!
        let globalCompsList = [];
        try {
          const { data: globalComps } = await supabase
            .from('product_components')
            .select('component:components(*)')
            .eq('product_id', productData.id);
          
          if (globalComps) {
            globalCompsList = globalComps.map(row => ({
              id: row.component?.id,
              name: row.component?.name,
              description: row.component?.description,
              dbComponentId: row.component?.id,
              price: 0,
              stockQuantity: 1,
              condition: 'New',
              glbUrl: row.component?.stl_file_path || ''
            })).filter(c => c.id);
          }
        } catch (globalErr) {
          console.error('Error loading global components:', globalErr);
        }

        // Merge global components with active seller components (prioritizing seller offerings!)
        const mergedComps = [...(dbComps || [])];
        globalCompsList.forEach(gComp => {
          // If this global component is not already represented by a seller offering, add it!
          if (!mergedComps.some(sComp => sComp.globalComponentId === gComp.id || sComp.dbComponentId === gComp.id)) {
            mergedComps.push(gComp);
          }
        });

        setDatabaseComponents(mergedComps);

        // Symmetrical Visual Substitute Pre-Loader:
        // If a shopper loads the visualizer directly from a seller product details card
        // or component catalog listing with a specific custom visual aftermarket substitute,
        // we must immediately map, construct, and pre-apply its custom .glb model live on the car!
        if (initialComponentId && dbComps && dbComps.length > 0) {
          const matched = dbComps.find(c => c.id?.toString() === initialComponentId || c.globalComponentId?.toString() === initialComponentId);
          if (matched && matched.glbUrl) {
            const baseName = matched.globalName || matched.name;
            if (baseName) {
              // Symmetrical URL Resolver: Bypasses 404 local host issues by resolving relative storage paths to CDN!
              let finalGlbUrl = matched.glbUrl;
              if (finalGlbUrl && !finalGlbUrl.startsWith('http')) {
                const { data: storageUrlData } = supabase.storage.from('product-3d-models').getPublicUrl(finalGlbUrl);
                finalGlbUrl = storageUrlData?.publicUrl || finalGlbUrl;
              }

              setComponentWraps(prev => ({
                ...prev,
                [baseName]: {
                  hidden: false,
                  componentName: matched.name,
                  subbedGlbUrl: finalGlbUrl,
                  position: matched.position || [0, 0, 0],
                  rotation: matched.rotation || [0, 0, 0],
                  scale: matched.scale || [1, 1, 1],
                  color: matched.color || null,
                  swapOptions: [{ glbUrl: finalGlbUrl, name: matched.name, sellerComponentId: matched.id }],
                  activeOptionIndex: 0
                }
              }));
              console.log('🎯 Symmetrical visual pre-loader applied custom visual substitute GLB model live on base car panel:', baseName);
            }
          }
        }

        // Set loading false early!
        // This unlocks the search bar, sidebar listings, and UI controls immediately
        // while the heavy 3D GLB model loads in the background!
        setLoading(false);

        const { data: settingsData } = await supabase.from('platform_settings').select('value').eq('key', 'watermark_settings').maybeSingle();
        if (settingsData && settingsData.value) {
            const dbUrl = settingsData.value.imageUrl || '/assets/images/OpenGadget-SMALL-GREY-TRANS-1763756659192.png';
            setWatermarkSettings({
                enabled: settingsData.value.enabled ?? true,
                imageUrl: sanitizeImageUrl(dbUrl)
            });
        }

      } catch (err) {
        console.error('Error loading ModelViewer data:', err);
        setError(err.message);
        setLoading(false); // Make sure to disable loading block on error!
      }
    };
    loadAllData();
  }, [modelId, sku, derivedSlug, reconstructedName, initialComponentId]);

  useEffect(() => {
    const fetchProductSellers = async () => {
      if (product?.id) {
        const { data } = await supabase
          .from('seller_products')
          .select(`
            id,
            price,
            seller_id,
            stock_amount,
            sell_product_only,
            seller_accounts (
              id,
              business_name,
              business_slug,
              rating
            )
          `)
          .eq('product_id', product.id)
          .ilike('status', 'active')
          .gt('price', 0)
          .or('isdeleted.is.null,isdeleted.eq.false');
        setProductSellers(data || []);
      }
    };
    fetchProductSellers();
  }, [product]);

  // Dynamic Long-tail Component SEO URL Rewriter
  useEffect(() => {
    if (product && initialComponentId && databaseComponents.length > 0) {
      const dbC = databaseComponents.find(c => c.id?.toString() === initialComponentId || c.globalComponentId?.toString() === initialComponentId);
      if (dbC) {
        // Build the clean long-tail SEO URL path
        const safeCat = (product.categoryName || product.category?.name || 'product').toLowerCase().replace(/[^a-z0-9]+/g, '-');
        let prefix = '/product';
        if (['automotive', 'motorcycles'].includes(safeCat)) prefix = '/vehicle';
        
        let processedName = product.name?.toLowerCase() || '';
        let year = '';
        if (prefix === '/vehicle') {
          const yearMatch = processedName.match(/\b(19\d{2}|20\d{2})\b/);
          if (yearMatch) {
            year = `/${yearMatch[1]}`;
            processedName = processedName.replace(yearMatch[0], '').trim();
          }
        }
        
        const pathSegments = processedName.replace(/[^a-z0-9\s.-]/g, '').trim().split(/\s+/).filter(Boolean).join('/');
        const cSlug = dbC.slug;
        
        let targetUrl = `${prefix}${year}/${pathSegments}`;
        if (cSlug) {
          targetUrl += `/${cSlug}`;
        }
        targetUrl += `/parts-diagram?componentId=${initialComponentId}`;
        
        // Push the clean long-tail SEO URL dynamically to the address bar without reloading!
        if (window.location.pathname !== targetUrl && !window.location.pathname.includes('/parts-diagram')) {
          window.history.replaceState(null, '', targetUrl);
        }
      }
    }
  }, [product, initialComponentId, databaseComponents]);

  // Lazy-Load Collaborative Annotations:
  // Only trigger the database fetch when the user actively toggles open the Annotations panel!
  useEffect(() => {
    const fetchAnnotations = async () => {
      const pId = modelId || product?.id;
      if (pId && showAnnotations) {
        try {
          const data = await annotationService.getByProductId(pId);
          setAnnotations(data || []);
        } catch (err) {
          console.error('Error fetching annotations on-demand:', err);
        }
      }
    };
    fetchAnnotations();
  }, [modelId, product?.id, showAnnotations]);

  const handleAddAnnotation = async (annotationData) => {
    if (!user) {
      alert("Please sign in to add annotations.");
      return;
    }
    const pId = modelId || product?.id;

    // Symmetrical UUID Validator: Prevents 22P02 Postgres parsing errors!
    const isUUID = (str) => typeof str === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(str);
    
    let compId = annotationData.componentId || selectedComponent?.dbComponentId || selectedComponent?.id || null;
    const finalCompName = annotationData.componentName || selectedComponent?.name || selectedComponent?.id || null;

    let searchName = compId;
    if (isUUID(compId)) {
      // It is already a valid UUID!
    } else {
      searchName = finalCompName || compId;
    }

    if (searchName && !isUUID(searchName)) {
      // Symmetrical DB Lookup Fallback: If the selected component ID is a WebGL node name string,
      // actively query the components table in Supabase to resolve its actual global components.id UUID!
      // We strip trailing Three.js loader unique suffixes (like _0001, _lodA, _Material) to ensure robust substring matches!
      try {
        let cleanName = searchName.replace(/_[0-9]+$/, '').trim();
        cleanName = cleanName.replace(/_Material_[0-9]+$/, '').trim();
        cleanName = cleanName.replace(/_lod[A-Z]$/i, '').trim();

        const { data: matchedComps, error: searchErr } = await supabase
          .from('components')
          .select('id, name')
          .or(`name.ilike.%${cleanName}%,name.ilike.%${cleanName.substring(0, 15)}%`)
          .limit(1);
          
        if (!searchErr && matchedComps && matchedComps.length > 0) {
          compId = matchedComps[0].id; // Successfully resolved to the correct UUID!
        } else {
          compId = null; // Fallback to generic model level
        }
      } catch (err) {
        console.warn('[handleAddAnnotation] Failed to resolve name string to UUID:', err);
        compId = null;
      }
    } else {
      compId = searchName;
    }

    try {
      const newAnnotation = await annotationService.create({
        productId: pId,
        componentId: compId,
        componentName: finalCompName,
        text: annotationData.text,
        position: annotationData.position
      });

      // Inject the component friendly name and details locally to guarantee instant, seamless UI updates!
      const populatedAnnotation = {
        ...newAnnotation,
        adminComment: newAnnotation.adminComment || finalCompName,
        component: newAnnotation.component || (compId ? { id: compId, name: finalCompName } : null)
      };

      setAnnotations(prev => [populatedAnnotation, ...prev]);
    } catch (err) {
      console.error('Error adding annotation:', err);
      alert('Failed to add annotation');
    }
  };

  const handleEditAnnotation = async (id, text) => {
    try {
      const updated = await annotationService.updateUserAnnotation(id, text);
      setAnnotations(prev => prev.map(a => a.id === id ? updated : a));
    } catch (err) {
      console.error('Error editing annotation:', err);
      alert('Failed to edit annotation: ' + err.message);
    }
  };

  const handleVoteAnnotation = async (id, type) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    try {
      const updated = await annotationService.vote(id, type);
      setAnnotations(prev => prev.map(ann => ann.id === id ? updated : ann));
      if (selectedAnnotationDetail && selectedAnnotationDetail.id === id) {
        setSelectedAnnotationDetail(updated);
      }
    } catch (err) {
      console.error('Error voting on annotation:', err);
      alert(err.message || 'Failed to submit vote');
    }
  };

  const handleDeleteAnnotation = async (id) => {
    try {
      await supabase.from('model_annotations').delete().eq('id', id);
      setAnnotations(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      console.error('Error deleting annotation:', err);
    }
  };

  // Enriches extracted components with database info
  const enrichedComponents = useMemo(() => {
    if (extractedComponents.length === 0) {
      // Symmetrical Fallback: If the 3D model hasn't rendered/loaded yet, build list from databaseComponents!
      // This unlocks the search panel and sidebar listings instantly while the 3D model loads in the background!
      return databaseComponents.map((dbComp) => ({
        id: dbComp.id || dbComp.globalComponentId,
        name: dbComp.customName || dbComp.name || dbComp.globalName || 'Unnamed Part',
        dbComponentId: dbComp.globalComponentId || dbComp.id || dbComp.dbComponentId,
        sellerComponentId: dbComp.id,
        price: dbComp.price || null,
        description: dbComp.description || '',
        customName: dbComp.customName || dbComp.name || dbComp.globalName || 'Unnamed Part',
        sellerId: dbComp.sellerId,
        sellerBusinessName: dbComp.storeName,
        is3dManufacturing: !!dbComp.is3dManufacturing,
        isCncCapable: !!dbComp.isCncCapable,
        isPainted: !!(dbComp.isPainted || dbComp.is_painted),
        is_painted: !!(dbComp.isPainted || dbComp.is_painted),
        isNotYetRendered: true, // Special tag! Indicates the 3D mesh is not yet rendered in Canvas
        availableOptions: [{
          id: dbComp.globalComponentId || dbComp.id || dbComp.dbComponentId,
          sellerComponentId: dbComp.id,
          customName: dbComp.customName,
          name: dbComp.name || dbComp.globalName,
          price: dbComp.price,
          sellerBusinessName: dbComp.storeName,
          sellerId: dbComp.sellerId,
          is3dManufacturing: dbComp.is3dManufacturing,
          isCncCapable: dbComp.isCncCapable,
          isPainted: dbComp.isPainted || dbComp.is_painted,
          is_painted: dbComp.isPainted || dbComp.is_painted,
        }]
      }));
    }
    
    return extractedComponents.map((extractedComp) => {
        // Prioritize matching the explicit global name stored in the component definition
        const matches = databaseComponents.filter(dbComp => {
          const extName = extractedComp.name?.toLowerCase().trim().replace(/[-_\s]+/g, '');
          const dbName = dbComp.globalName?.toLowerCase().trim().replace(/[-_\s]+/g, '');
          const dbCustomName = dbComp.customName?.toLowerCase().trim().replace(/[-_\s]+/g, '');

          if (!extName) return false;

          // Robust fuzzy and exact checks
          if (dbName === extName) return true;
          if (dbCustomName === extName) return true;
          if (dbName && extName.includes(dbName)) return true;
          if (dbName && dbName.includes(extName)) return true;

          return false;
        });
      
      if (matches.length > 0) {
        const primaryMatch = matches[0];
        return {
          ...extractedComp,
          dbComponentId: primaryMatch.globalComponentId,
          sellerComponentId: primaryMatch.id,
          price: primaryMatch.price || null,
          description: primaryMatch.description || '',
          customName: primaryMatch.customName || extractedComp.name,
          sellerId: primaryMatch.sellerId,
          sellerBusinessName: primaryMatch.storeName,
          is3dManufacturing: matches.some(m => m.is3dManufacturing),
          isCncCapable: matches.some(m => m.isCncCapable),
          isPainted: matches.some(m => m.isPainted || m.is_painted),
          is_painted: matches.some(m => m.isPainted || m.is_painted),
          availableOptions: matches.map(m => ({
            id: m.globalComponentId,
            sellerComponentId: m.id,
            customName: m.customName,
            name: m.name,
            price: m.price,
            sellerBusinessName: m.storeName,
            sellerId: m.sellerId,
            is3dManufacturing: m.is3dManufacturing,
            isCncCapable: m.isCncCapable,
            isPainted: m.isPainted || m.is_painted,
            is_painted: m.isPainted || m.is_painted,
            mfg3dMaterials: m.mfg3dMaterials,
            mfg3dColors: m.mfg3dColors,
            mfgCncMaterials: m.mfgCncMaterials,
            mfgCncColors: m.mfgCncColors,
            paintedColors: m.paintedColors,
            painted_colors: m.painted_colors || m.paintedColors
          })),
        };
      }
      return { ...extractedComp, availableOptions: [] };
    });
  }, [extractedComponents, databaseComponents]);

  // --- Symmetrical Component Detail Modal States & Sourcing Logic ---
  // Declared symmetrically BELOW enrichedComponents to completely prevent any circular Temporal Dead Zone (TDZ) reference errors!
  const [isPartDetailModalOpen, setIsPartDetailModalOpen] = useState(false);
  const [partDetailComponent, setPartDetailComponent] = useState(null);
  const [partSellers, setPartSellers] = useState([]);
  const [isPartSellersLoading, setIsPartSellersLoading] = useState(false);
  const [sourcingQuantity, setSourcingQuantity] = useState(1);
  const [sourcingNotes, setSourcingNotes] = useState('');
  const [partPhoto, setPartPhoto] = useState(null);
  const [partPhotoPreview, setPartPhotoPreview] = useState('');
  const [neededByDate, setNeededByDate] = useState('');
  const [emailNotification, setEmailNotification] = useState(true);
  const [isSubmittingSourcing, setIsSubmittingSourcing] = useState(false);
  const [sourcingSuccessMessage, setSourcingSuccessMessage] = useState('');

  // Symmetrical Double-Click / Right-Click Detail Modal Trigger:
  // Loads metadata, fetches active seller listings, and opens the exact, correct Part Specifications & Logistics modal directly!
  const handleComponentDoubleClick = useCallback(async (component) => {
    const enrichedComp = enrichedComponents.find(c => c.id === component.id || c.name === component.name) || component;
    setPartDetailComponent(enrichedComp);
    
    let dbCompId = enrichedComp.dbComponentId || enrichedComp.id;
    const isUuid = (str) => typeof str === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);

    // Self-Healing Name Lookup Fallback:
    if (!isUuid(dbCompId) && enrichedComp.name) {
      try {
        const { data: matchedComp } = await supabase
          .from('components')
          .select('id')
          .eq('name', enrichedComp.name)
          .limit(1)
          .maybeSingle();
        if (matchedComp && isUuid(matchedComp.id)) {
          dbCompId = matchedComp.id;
        }
      } catch (err) {
        console.warn('Name lookup failed:', err);
      }
    }

    try {
      let activeSellers = [];
      if (isUuid(dbCompId)) {
        const { data: sellers } = await supabase
          .from('seller_components')
          .select(`
            id, price, stock_quantity, purchase_limit, condition, custom_name,
            is_self_manufacturing, manufacturer_id,
            seller:seller_accounts(id, business_name, business_slug, rating)
          `)
          .eq('component_id', dbCompId)
          .or('isdeleted.is.null,isdeleted.eq.false');
        activeSellers = sellers || [];
      }
      
      if (activeSellers.length > 0) {
        setDoubleClickedSellers(activeSellers);
        const bestSeller = activeSellers[0];
        const fullOffering = await componentService.getById(bestSeller.id);
        
        setSelectedComponentOffering({
          ...fullOffering,
          globalItem: fullOffering.component
        });
        setIsOfferingModalOpen(true);
      } else {
        setDoubleClickedSellers([]);
        // Fallback: No active sellers, show the parts request form natively inside SellerOfferingDetailModal!
        const dummyOffering = {
          id: 'sourcing',
          isSourcingRequest: true,
          name: enrichedComp.name || 'Selected Component',
          component: enrichedComp,
          globalItem: enrichedComp,
          productId: product?.id || null,
          productName: product?.name || null,
          price: null
        };
        setSelectedComponentOffering(dummyOffering);
        setIsOfferingModalOpen(true);
      }
    } catch (err) {
      console.error('Failed to load part sellers on double-click:', err);
      setDoubleClickedSellers([]);
      // Fallback: Show parts request form natively inside SellerOfferingDetailModal!
      const dummyOffering = {
        id: 'sourcing',
        isSourcingRequest: true,
        name: enrichedComp.name || 'Selected Component',
        component: enrichedComp,
        globalItem: enrichedComp,
        productId: product?.id || null,
        productName: product?.name || null,
        price: null
      };
      setSelectedComponentOffering(dummyOffering);
      setIsOfferingModalOpen(true);
    }
  }, [enrichedComponents]);

  // Symmetrical Decoupled Sellers Loader for fallback sourcing modal
  useEffect(() => {
    if (!partDetailComponent) return;

    const fetchPartSellers = async () => {
      try {
        setIsPartSellersLoading(true);
        
        // Symmetrical Bypass for Non-Chunked Models:
        // Since un-chunked fallback components do not exist as registered standalone components in the DB,
        // we completely skip the heavy 12-second database self-healing query and immediately render the sourcing form!
        if (!parentIsChunked) {
          console.warn('⚠️ [Sourcing] Non-chunked model fallback. Skipping database search and launching sourcing form!');
          setPartSellers([]);
          setIsPartSellersLoading(false);
          
          const dummyOffering = {
            id: 'sourcing',
            isSourcingRequest: true,
            name: partDetailComponent.name || 'Selected Component',
            component: partDetailComponent,
            globalItem: partDetailComponent,
            productId: product?.id || null,
            productName: product?.name || null,
            price: null
          };
          setSelectedComponentOffering(dummyOffering);
          setIsOfferingModalOpen(true);
          return;
        }

        let dbCompId = partDetailComponent.dbComponentId;
        const isUuid = (str) => typeof str === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);

        // Self-Healing Name Lookup Fallback:
        if (!isUuid(dbCompId) && partDetailComponent.name) {
          const { data: matchedComp } = await supabase
            .from('components')
            .select('id')
            .eq('name', partDetailComponent.name)
            .limit(1)
            .maybeSingle();
          if (matchedComp && isUuid(matchedComp.id)) {
            dbCompId = matchedComp.id;
          }
        }

        if (isUuid(dbCompId)) {
          // Query active seller inventories for this specific part
          const { data: sellers, error: sellersErr } = await supabase
            .from('seller_components')
            .select(`
              id, price, stock_quantity, purchase_limit, condition, custom_name,
              is_self_manufacturing, manufacturer_id,
              seller:seller_accounts(id, business_name, business_slug, rating)
            `)
            .eq('component_id', dbCompId)
            .or('isdeleted.is.null,isdeleted.eq.false');

          if (!sellersErr && sellers) {
            setPartSellers(sellers);
          }
        }
      } catch (err) {
        console.error('Error fetching part sellers:', err);
      } finally {
        setIsPartSellersLoading(false);
      }
    };

    fetchPartSellers();
  }, [partDetailComponent]);

  const handleSourcingRequestSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    setIsSubmittingSourcing(true);
    setSourcingSuccessMessage('');

    try {
      let uploadedPhotoUrls = [];
      if (partPhoto) {
        // Symmetrical Lazy Load to prevent top-level circular TDZ evaluation errors!
        const { imageUploadService: lazyUploader } = await import('../../utils/imageUploadService');
        
        // Upload images to Supabase storage inside the public 'component-images' bucket
        const folderPath = `part-requests/${user.id}/${Date.now()}`;
        const uploadedPaths = await lazyUploader.uploadImages([partPhoto], folderPath);
        uploadedPhotoUrls = uploadedPaths.map(path => lazyUploader.getPublicUrl(path));
      }

      // Symmetrical part_requests insert query to record the part request natively!
      const { error: insertErr } = await supabase
        .from('part_requests')
        .insert([{
          user_id: user.id,
          model_id: product?.id,
          model_name: product?.name || 'Unknown Model',
          part_name: partDetailComponent?.name || 'Custom Component',
          note: sourcingNotes || `Requesting part with quantity: ${sourcingQuantity}`,
          needed_by_date: neededByDate || null,
          email_notification: !!emailNotification,
          photos: uploadedPhotoUrls
        }]);

      if (insertErr) throw insertErr;

      // Track user login session activity symmetrically
      await userService.recordActivity(
        user.id,
        navigator.userAgent,
        'secure_action',
        `Part request submitted for component: ${partDetailComponent?.name || 'Custom Component'} (qty: ${sourcingQuantity})`,
        product?.id || null,
        partDetailComponent?.dbComponentId || partDetailComponent?.id || null
      ).catch(e => console.warn('Activity tracking error:', e));

      setSourcingSuccessMessage('Part request submitted successfully! Our suppliers will contact you soon.');
    } catch (err) {
      console.error('Error submitting part request:', err);
      setSourcingSuccessMessage(`Error: ${err.message || 'Failed to submit request'}`);
    } finally {
      setIsSubmittingSourcing(false);
    }
  };

  // Handle URL-provided component selection
  const [initialSelectionDone, setInitialSelectionDone] = useState(false);
  useEffect(() => {
    if (enrichedComponents.length > 0 && (initialComponentId || initialComponentName)) {
      if (initialSelectionDone && selectedComponent && !selectedComponent.isNotYetRendered) {
        return; // Fully matched to 3D mesh
      }

      let comp = enrichedComponents.find(c => {
        if (initialComponentId) {
           const targetId = initialComponentId.toLowerCase();
           if (c.id?.toString()?.toLowerCase() === targetId) return true;
           if (c.dbComponentId?.toString()?.toLowerCase() === targetId) return true;
           if (c.globalComponentId?.toString()?.toLowerCase() === targetId) return true;
           if (c.sellerComponentId?.toString()?.toLowerCase() === targetId) return true;
           if (c.availableOptions?.some(opt => 
             opt.id?.toString()?.toLowerCase() === targetId || 
             opt.sellerComponentId?.toString()?.toLowerCase() === targetId
           )) return true;
        }
        if (initialComponentName) {
           const normalizedName = initialComponentName.toLowerCase().replace(/-/g, ' ');
           if (c.name?.toLowerCase() === normalizedName) return true;
           if (c.customName?.toLowerCase() === normalizedName) return true;
        }
        return false;
      });
      
      // Fallback: check databaseComponents to see if we can match by name or global ID
      if (!comp && initialComponentId && databaseComponents.length > 0) {
        const targetId = initialComponentId.toLowerCase();
        const dbTarget = databaseComponents.find(dbC => 
          dbC.id?.toString()?.toLowerCase() === targetId || 
          dbC.globalComponentId?.toString()?.toLowerCase() === targetId ||
          dbC.sellerComponentId?.toString()?.toLowerCase() === targetId
        );
        if (dbTarget) {
          comp = enrichedComponents.find(c => 
            c.name === dbTarget.globalName || 
            c.name === dbTarget.name || 
            c.customName === dbTarget.name || 
            c.name === dbTarget.customName || 
            c.dbComponentId === dbTarget.globalComponentId ||
            c.dbComponentId === dbTarget.id
          );
        }
      }

      if (comp) {
        setSelectedComponent(comp);
        setInitialSelectionDone(true);
        const urlMode = searchParams.get('viewMode');
        setViewMode(urlMode || 'x-ray-gray');
      }
    }
  }, [enrichedComponents, databaseComponents, initialComponentId, initialComponentName, initialSelectionDone, searchParams, selectedComponent]);

  const handleToggleZRay = useCallback((component) => {
    if (!component) return;
    const compId = component.id;
    const compName = component.name;
    const compClean = component.name ? component.name.replace(/[^a-zA-Z0-9]/g, '').toLowerCase().replace(/\d+$/, '') : '';
    
    // When activating individual component Z-Ray, ensure global viewMode is shaded so the rest of the model renders solid/normal
    if (viewMode === 'x-ray-gray' || viewMode === 'x-ray') {
      setViewMode('shaded');
    }

    setZRayComponentIds(prev => {
      const isAlreadyInZRay = prev.includes(compId) || (compName && prev.includes(compName)) || (compClean && prev.includes(compClean));
      if (isAlreadyInZRay) {
        // Toggle OFF Z-Ray for this component
        return prev.filter(id => id !== compId && id !== compName && id !== compClean);
      } else {
        // Toggle ON Z-Ray ONLY for this selected component!
        const additions = [compId];
        if (compName) additions.push(compName);
        if (compClean) additions.push(compClean);
        if (component.dbComponentId) additions.push(component.dbComponentId);
        return [...prev, ...additions];
      }
    });
  }, [viewMode]);

  const handleComponentsExtracted = useCallback((rawComponents) => {
    if (rawComponents) {
      setExtractedComponents(rawComponents);
      const initialVisibility = {};
      rawComponents.forEach(c => { initialVisibility[c.id] = true; });
      setComponentVisibility(initialVisibility);
    }
  }, []);

  const handleComponentSelect = useCallback((component, isCtrlPressed = false) => {
    if (!component) {
      if (!isMultiSelectActive) {
        setSelectedComponent(null);
        setSelectedComponentIds([]);
      }
      return;
    }

    const isUUID = (str) => typeof str === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(str);

    let resolvedComp;

    if (isUUID(component.globalComponentId) || isUUID(component.dbComponentId) || isUUID(component.id)) {
      // Symmetrical Fast-Pass: If the selected component is already a database component
      // (meaning its ID is a valid Postgres UUID), bypass name resolution and preserve it!
      const inputId = component.globalComponentId || component.dbComponentId || component.id;
      const matchedDb = databaseComponents.find(dbC => dbC.id === inputId || dbC.globalComponentId === inputId);
      
      const targetId = matchedDb?.globalComponentId || matchedDb?.id || inputId;

      resolvedComp = {
        ...component,
        dbComponentId: targetId,
        name: matchedDb?.name || component.name || component.customName || 'Unnamed Part'
      };
    } else {
      // Symmetrical Database Component Resolver:
      // When a shopper clicks any mesh node on the 3D canvas, we must securely resolve
      // its WebGL node name (e.g. "bod2yBadge_Geo...") to its actual Postgres database UUID!
      const matched = databaseComponents.find(dbC => {
        const normalizeName = (str) => {
          if (!str) return '';
          return str
            .toLowerCase()
            .trim()
            .replace(/\.[0-9]+$/, '')    // Strip Three.js .001 index
            .replace(/_[0-9]+$/, '')     // Strip Three.js _0001 index
            .replace(/_material_[0-9]+$/i, '') // Strip Three.js _Material_0 index
            .replace(/_loda$/i, '')       // Strip _lodA
            .replace(/_lodb$/i, '');      // Strip _lodB
        };
        
        const nodeNormalized = normalizeName(component.id || component.name);
        const dbNormalized = normalizeName(dbC.name || dbC.customName || dbC.globalName);
        
        return nodeNormalized && dbNormalized && nodeNormalized === dbNormalized;
      });

      resolvedComp = {
        ...component,
        dbComponentId: matched?.globalComponentId || matched?.id || component.dbComponentId || null,
        name: matched?.name || component.name || component.id
      };
    }

    // Toggle multi-select mode automatically if CTRL key is pressed, or if isMultiSelectActive is true!
    const activeMulti = isMultiSelectActive || isCtrlPressed;
    if (mode === 'wrap' && activeMulti) {
      const compId = resolvedComp.id;

      // If CTRL was pressed but isMultiSelectActive was false, activate it cleanly!
      if (isCtrlPressed && !isMultiSelectActive) {
        setIsMultiSelectActive(true);
      }

      setSelectedComponentIds(prev => {
        let updated;
        if (prev.includes(compId)) {
          updated = prev.filter(id => id !== compId);
        } else {
          updated = [...prev, compId];
        }

        if (updated.length > 0) {
          setSelectedComponent(resolvedComp);
        } else {
          setSelectedComponent(null);
        }
        return updated;
      });
      setShowModelInfo(true);
    } else {
      setSelectedComponent(prev => (prev?.id === resolvedComp.id ? null : resolvedComp));
      setSelectedComponentIds(resolvedComp ? [resolvedComp.id] : []);
      if (resolvedComp) {
        setShowModelInfo(true); // Automatically expand the sidebar when a mesh is selected!
      }
    }
  }, [mode, isMultiSelectActive, databaseComponents]);
  
  const handleVisibilityChange = useCallback((visibilityMap) => {
    setComponentVisibility(visibilityMap);
  }, []);

  const handleIsolateComponent = useCallback((component) => {
    const compId = typeof component === 'object' ? component.id : component;
    const isIsolated = enrichedComponents.every(c => (componentVisibility[c.id] ?? true) === (c.id === compId));
    const newVisibility = {};
    if (isIsolated) {
      enrichedComponents.forEach(c => {
        newVisibility[c.id] = true;
        if (c.name) {
          newVisibility[c.name] = true;
          newVisibility[c.name.toLowerCase()] = true;
          const cln = c.name.replace(/[^a-zA-Z0-9]/g, '').toLowerCase().replace(/\d+$/, '');
          if (cln) newVisibility[cln] = true;
        }
        if (c.dbComponentId) newVisibility[c.dbComponentId] = true;
      });
    } else {
      enrichedComponents.forEach(c => {
        const isTarget = c.id === compId;
        newVisibility[c.id] = isTarget;
        if (c.name) {
          newVisibility[c.name] = isTarget;
          newVisibility[c.name.toLowerCase()] = isTarget;
          const cln = c.name.replace(/[^a-zA-Z0-9]/g, '').toLowerCase().replace(/\d+$/, '');
          if (cln) newVisibility[cln] = isTarget;
        }
        if (c.dbComponentId) newVisibility[c.dbComponentId] = isTarget;
      });
    }
    setComponentVisibility(newVisibility);
  }, [enrichedComponents, componentVisibility]);
  
  const handleToggleComponentVisibility = useCallback((component) => {
    const compId = typeof component === 'object' ? component.id : component;
    const compObj = typeof component === 'object' ? component : enrichedComponents.find(c => c.id === compId || c.name === compId);
    const currentVis = componentVisibility[compId] ?? true;
    const nextVis = !currentVis;
    setComponentVisibility(p => {
      const next = { ...p, [compId]: nextVis };
      if (compObj) {
        if (compObj.name) {
          next[compObj.name] = nextVis;
          next[compObj.name.toLowerCase()] = nextVis;
          const cln = compObj.name.replace(/[^a-zA-Z0-9]/g, '').toLowerCase().replace(/\d+$/, '');
          if (cln) next[cln] = nextVis;
        }
        if (compObj.dbComponentId) next[compObj.dbComponentId] = nextVis;
      }
      return next;
    });
  }, [enrichedComponents, componentVisibility]);


  useEffect(() => {
    if (product?.id || initialComponentId) {
       userService.recordActivity(
           user?.id || null,
           navigator.userAgent,
           initialComponentId ? 'Component View' : 'Model View',
           window.location.pathname + window.location.search,
           initialComponentId ? null : product?.id,
           initialComponentId || null
       ).catch(e => console.warn('Activity tracking error:', e));
    }
  }, [user?.id, product?.id, initialComponentId]);

  // Track manual component selection in the 3D viewer
  useEffect(() => {
    if (selectedComponent?.dbComponentId) {
      userService.recordActivity(
        user?.id || null,
        navigator.userAgent,
        'Component View',
        `3D Viewer - Selected Part: ${selectedComponent.name || 'Unnamed'}`,
        product?.id || null,
        selectedComponent.dbComponentId
      ).catch(e => console.warn('Activity tracking error:', e));
    }
  }, [selectedComponent?.dbComponentId, user?.id, product?.id]);

  const handleMaterialColorChange = (color) => {
    if (selectedComponent) {
      // Symmetrically apply color ONLY to the selected component and clear wildcard '*' to prevent painting the entire model!
      const targetId = selectedComponent.globalComponentId || selectedComponent.id;
      setComponentColors(prev => {
        const updated = { ...prev, [targetId]: color };
        delete updated['*'];
        delete updated['all'];
        delete updated['global'];
        return updated;
      });
    } else {
      // Symmetrically apply color to the ENTIRE model (using the wildcard '*')
      setComponentColors({ '*': color });
    }
  };

  const handleRemoveSelectedId = useCallback((id) => {
    setSelectedComponentIds(prev => {
      const updated = prev.filter(item => item !== id);
      if (updated.length === 0) {
        setSelectedComponent(null);
      } else if (selectedComponent?.name === id || selectedComponent?.id === id) {
        const first = enrichedComponents?.find(c => c.name === updated[0] || c.id === updated[0]);
        setSelectedComponent(first || { id: updated[0], name: updated[0] });
      }
      return updated;
    });
  }, [selectedComponent, enrichedComponents]);

  const handleToggleMultiSelect = useCallback((active) => {
    setIsMultiSelectActive(active);
    if (!active) {
      setSelectedComponentIds(selectedComponent ? [selectedComponent.id] : []);
    } else if (selectedComponent) {
      setSelectedComponentIds([selectedComponent.id]);
    }
  }, [selectedComponent]);

  useEffect(() => {
    setIsMultiSelectActive(false);
    setSelectedComponentIds([]);
  }, [mode]);

  const handleUpdateComponentWrap = useCallback((componentId, wrapFields) => {
    if (componentId === '_apply_bulk') {
      if (showBeforeWrap) {
        setComponentWrapsBefore(wrapFields || {});
      } else {
        setComponentWraps(wrapFields || {});
      }
      return;
    }
    
    if (componentId === '_reset') {
      if (showBeforeWrap) {
        setComponentWrapsBefore({});
      } else {
        setComponentWraps({});
      }
      return;
    }

    // Symmetrical Bulk Edit Sync:
    // If multi-select is active, apply the changes individually to ALL selected panels!
    const targets = ((mode === 'wrap' || mode === 'swap') && isMultiSelectActive && selectedComponentIds.length > 0)
      ? selectedComponentIds
      : [componentId];

    targets.forEach(targetKey => {
      const compObj = enrichedComponents?.find(c => c.id === targetKey || c.dbComponentId === targetKey);
      const compName = compObj?.name || selectedComponent?.name || targetKey;

      if (showBeforeWrap) {
        setComponentWrapsBefore(prev => ({
          ...prev,
          [targetKey]: {
            ...(prev[targetKey] || {}),
            ...wrapFields,
            componentName: compName
          }
        }));
      } else {
        setComponentWraps(prev => ({
          ...prev,
          [targetKey]: {
            ...(prev[targetKey] || {}),
            ...wrapFields,
            componentName: compName
          }
        }));
      }
    });
  }, [showBeforeWrap, enrichedComponents, selectedComponent, isMultiSelectActive, selectedComponentIds, mode]);

  const handleRemoveComponentWrap = useCallback((componentId) => {
    const compObj = enrichedComponents?.find(c => c.id === componentId || c.dbComponentId === componentId);
    const compName = compObj?.name || selectedComponent?.name || componentId;
    const key = compName;
    
    if (showBeforeWrap) {
      setComponentWrapsBefore(prev => {
        const copy = { ...prev };
        delete copy[key];
        delete copy[componentId]; // delete both to be safe and backward compatible!
        return copy;
      });
    } else {
      setComponentWraps(prev => {
        const copy = { ...prev };
        delete copy[key];
        delete copy[componentId]; // delete both to be safe and backward compatible!
        return copy;
      });
    }
  }, [showBeforeWrap, enrichedComponents, selectedComponent]);

  const handleSaveWrapConfig = useCallback(async (configName) => {
    if (!user) {
      alert('You must be logged in to save custom livery designs.');
      return;
    }
    try {
      // Symmetrical Upsert Guard:
      // Check if there is already an existing wrap config row with the exact same project name, product, and shop.
      // If so, update that row instead of inserting a duplicate!
      let targetId = configId;
      if (!targetId) {
        const { data: existing } = await supabase
          .from('custom_wrap_configs')
          .select('id')
          .eq('shop_id', user.id)
          .eq('product_id', product.id)
          .eq('config_name', configName)
          .maybeSingle();
        if (existing) targetId = existing.id;
      }

      const payload = {
        shop_id: user.id,
        product_id: product.id,
        config_name: configName,
        config_data: {
          before: componentWrapsBefore,
          after: componentWraps,
          notes: wrapNotes
        }
      };

      if (mode !== 'swap') {
        payload.assembly_id = assemblyId || null;
      }

      const table = mode === 'swap' ? 'seller_swap_configs' : 'custom_wrap_configs';
      let result;
      if (targetId) {
        result = await supabase
          .from(table)
          .update(payload)
          .eq('id', targetId);
      } else {
        result = await supabase
          .from(table)
          .insert(payload);
      }

      if (result.error) throw result.error;
      setSavedProjectName(configName);
      setShowWrapSuccessModal(true);
    } catch (err) {
      console.error('Error saving config:', err);
      alert('Failed to save configuration: ' + err.message);
    }
  }, [user, product?.id, componentWraps, componentWrapsBefore, wrapNotes, configId, assemblyId, mode]);

  const viewerModel = useMemo(() => ({
    ...product,
    modelUrl: product?.modelUrl,
    components: enrichedComponents,
  }), [product, enrichedComponents]);

  // Symmetrical B2B Shop Repair Log Save Actions
  const handleLogRepairTrigger = useCallback((comp) => {
    setRepairForm({
      status: 'needs_repair',
      cost: '0.00',
      notes: `Service required for ${comp.name}`,
      resolution: ''
    });
    setShowLogRepairModal(true);
  }, []);

  const handleSaveRepairLog = async (e) => {
    e.preventDefault();
    if (!selectedComponent?.name || !assemblyId) return;
    try {
      const { data, error } = await supabase
        .from('assembly_service_records')
        .insert({
          assembly_id: assemblyId,
          component_id: selectedComponent.name,
          status: repairForm.status,
          cost: parseFloat(repairForm.cost || 0),
          notes: repairForm.notes,
          resolution: repairForm.resolution
        })
        .select()
        .single();
      if (error) throw error;
      if (data) {
        setRepairLogs(prev => [data, ...prev]);
        setShowLogRepairModal(false);
        alert('Service logged successfully!');
      }
    } catch (err) {
      console.error('Error saving service record:', err);
      alert('Failed to log service: ' + err.message);
    }
  };


  const handleReset = useCallback(() => {
    setSelectedComponent(null);
    setSelectedComponentIds([]);
    setZRayComponentIds([]);
    setComponentColors({});
    setComponentVisibility({});
    setViewMode('shaded');
    setIsExploded(false);
    setExplodeScaleX(1);
    setExplodeScaleY(1);
    setExplodeScaleZ(1);
    setIsExplodeLocked(true);
    setMobileSearchTerm('');
    setMeasurements([]);
    setActiveMeasurementTool(null);
    setIsInfiniteVertical(false); // Disable infinite vertical orbit on reset!
    if (canvasRef.current) {
      if (canvasRef.current.resetCamera) {
        canvasRef.current.resetCamera();
      }
      if (canvasRef.current.resetMaterials) {
        canvasRef.current.resetMaterials();
      }
    }
    const params = new URLSearchParams(window.location.search);
    params.delete('componentId');
    params.delete('viewMode');
    const newRelativePathQuery = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
    window.history.replaceState(null, '', newRelativePathQuery);

    setLighting({
      intensity: 75,
      temperature: 5500,
      shadowSoftness: 50,
      environment: 30,
      ambientOcclusion: 40,
      preset: 'studio',
      showEnvironmentBackground: false,
    });
    // This will implicitly restore all materials
  }, []);

  const handleScreenshot = useCallback(() => {
    if (!user) {
        setShowAuthModal(true);
        return;
    }

    if (user?.id && product?.id) {
       userService.recordActivity(
           user.id,
           navigator.userAgent,
           'Screenshot',
           window.location.pathname + window.location.search,
           product.id
       ).catch(e => console.warn('Activity tracking error:', e));
    }

    const canvas = document.querySelector('canvas');
    if (!canvas) return;

    if (!watermarkSettings.enabled || !watermarkSettings.imageUrl) {
        const dataUrl = canvas.toDataURL('image/png');
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = `${product?.name || '3d-model'}-screenshot.png`;
        a.click();
        return;
    }

    // Composite with watermark
    const compositeCanvas = document.createElement('canvas');
    compositeCanvas.width = canvas.width;
    compositeCanvas.height = canvas.height;
    const ctx = compositeCanvas.getContext('2d');
    
    // Draw 3D scene
    ctx.drawImage(canvas, 0, 0);

    // Draw Watermark
    const img = new window.Image();
    img.crossOrigin = 'anonymous'; // Important for external URLs
    img.onload = () => {
        // Calculate watermark size (e.g., 20% of canvas width or a max size)
        const margin = 20;
        const targetWidth = Math.min(compositeCanvas.width * 0.25, 300); 
        const aspectRatio = img.height / img.width;
        const targetHeight = targetWidth * aspectRatio;
        
        // Position bottom right
        const x = compositeCanvas.width - targetWidth - margin;
        const y = compositeCanvas.height - targetHeight - margin;
        
        ctx.globalAlpha = 0.8; // slightly transparent
        ctx.drawImage(img, x, y, targetWidth, targetHeight);
        
        const dataUrl = compositeCanvas.toDataURL('image/png');
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = `${product?.name || '3d-model'}-screenshot.png`;
        a.click();
    };
    img.onerror = () => {
        // Fallback to original if watermark fails to load
        const dataUrl = canvas.toDataURL('image/png');
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = `${product?.name || '3d-model'}-screenshot.png`;
        a.click();
    };
    img.src = watermarkSettings.imageUrl;

  }, [product, user, watermarkSettings]);

  const handleShare = useCallback(() => {
    navigator.clipboard.writeText(window.location.href)
      .then(() => alert('Link copied to clipboard!'))
      .catch(() => alert('Failed to copy link.'));
  }, []);

  // Construct SEO Metadata
  const getSeoMetadata = () => {
    const defaultTitle = product?.name || '3D Model Viewer';
    const defaultDesc = product?.description || `View and interact with ${product?.name || 'this 3D model'} on OpenGadget.`;
    
    if (derivedSlug) {
        if (initialComponentName) {
            const formattedPart = initialComponentName.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            return {
                title: `${formattedPart} for ${product?.name || derivedSlug} | 3D Viewer`,
                description: `View 3D model and details of ${formattedPart} for the ${product?.name || derivedSlug}.`
            };
        }
        
        let titleIntent = '3D Parts & Repair Model';
        if (derivedIntent === 'parts-diagram') titleIntent = 'Parts Diagram | Interactive 3D Viewer';
        if (derivedIntent.includes('parts-list')) {
            const countMatch = derivedIntent.match(/(\d+)-parts-list/);
            const count = countMatch ? countMatch[1] : (product?.componentCount || 'All');
            titleIntent = `${count} Parts List`;
        }

        return {
            title: `${product?.name || derivedSlug} ${titleIntent}`,
            description: `Explore the 3D model, parts, and specifications for ${product?.name || derivedSlug} on OpenGadget.`
        };
    }

    return { title: `${defaultTitle} | OpenGadget`, description: defaultDesc };
  };

  const seoMeta = getSeoMetadata();

  if (loading) return <div className="min-h-screen flex justify-center items-center">Loading...</div>;
  if (error) return <div className="min-h-screen flex justify-center items-center text-destructive flex-col gap-4">
    <Icon name="AlertCircle" size={48} />
    <p>Error: {error}</p>
    <Button onClick={() => navigate('/model-catalog')}>Return to Catalog</Button>
  </div>;
  if (!product) return <div className="min-h-screen flex justify-center items-center">Product not found.</div>;

  return (
    <div className="h-screen w-full bg-background overflow-hidden relative">
      {!isFullscreen && <Header />}
      
      {isMobile && !isFullscreen && (
        <MobileViewerHeader 
          modelName={product?.name}
          searchTerm={mobileSearchTerm}
          onSearchChange={setMobileSearchTerm}
          onReset={handleReset}
          onOpenPartsDrawer={() => setShowMobilePartsDrawer(true)}
          onOpenSpecs={() => setShowMobileSpecs(true)}
          selectedComponent={selectedComponent}
          componentVisibility={componentVisibility}
          onToggleVisibility={handleToggleComponentVisibility}
          onIsolate={handleIsolateComponent}
        />
      )}
      <main 
        className={`${isFullscreen ? 'h-[100dvh]' : (isMobile ? 'h-[100dvh] pt-[220px] pb-[80px]' : 'absolute top-[104px] bottom-0 left-0 right-0')} overflow-hidden w-full`}
        onContextMenu={(e) => e.preventDefault()}
      >
        <ViewerCanvas
          key={product.id}
          ref={canvasRef}
          modelUrl={product.modelUrl}
          model={viewerModel}
          isChunked={parentIsChunked}
          chunkProgress={chunkProgress}
          setChunkProgress={setChunkProgress}
          selectedComponent={selectedComponent}
          zRayComponentIds={zRayComponentIds}
          onComponentDoubleClick={handleComponentDoubleClick}
          onContextMenuRequest={(comp, x, y) => {
            setContextMenu({ show: true, x, y, component: comp });
          }}
          isInfiniteVertical={isInfiniteVertical}
          viewMode={viewMode}
          lighting={lighting}
          showGrid={showGrid}
          isFullscreen={isFullscreen}
          onComponentClick={handleComponentSelect}
          onComponentsExtracted={handleComponentsExtracted}
          componentVisibility={componentVisibility}
          componentColors={componentColors}
          componentWraps={showBeforeWrap ? componentWrapsBefore : componentWraps}
          onUpdateComponentWrap={handleUpdateComponentWrap} // Symmetrical: Pass state update handler down!
          isPlaying={isPlaying}
          isExploded={isExploded}
          explodeScaleX={explodeScaleX}
          explodeScaleY={explodeScaleY}
          explodeScaleZ={explodeScaleZ}
          activeMeasurementTool={activeMeasurementTool}
          measurements={measurements}
          onAddMeasurement={(m) => setMeasurements(prev => [...prev, m])}
          selectedMeasurementId={selectedMeasurementId}
          onUpdateMeasurement={(id, updatedFields) => {
            setMeasurements(prev => prev.map(m => m.id === id ? { ...m, ...updatedFields } : m));
          }}
          unit={unit}
          diagnosticRecords={diagnosticMap}
          isMultiSelectActive={isMultiSelectActive}
          selectedComponentIds={selectedComponentIds}
          gizmoMode={repairBuilderActive ? repairGizmoMode : gizmoMode}
          setGizmoMode={setGizmoMode}
          gizmoSize={gizmoSize} // Symmetrical: Pass active gizmo size scale factor down!
          onGizmoSizeChange={setGizmoSize} // Symmetrical: Pass active gizmo size updater down!
          repairGuideOffsets={repairGuideOffsets}
          repairBuilderActive={repairBuilderActive}
          onRepairPartMoved={handleRepairPartMoved}
          activeStepIndex={activeStepIndex}
          activeStepCamera={activeStepCamera}
          showStringLines={showStringLines}
          activeStepActions={repairBuilderActive ? activeStepActions : (activeStep?.actions || [])}
        />

        {/* Symmetrical High-Tech Top-Center HUD Status Indicator Overlay */}
        {/* Rendered natively inside parent page DOM context with a huge z-[9999] layer */}
        {/* to fully guarantee that WebGL never submerges, covers, or floats it! */}
        {hudState !== 'hidden' && (
          <div className={`absolute top-24 left-1/2 -translate-x-1/2 z-[9999] pointer-events-none flex items-center justify-center transition-all duration-1000 ${
            hudState === 'fadeout' ? 'opacity-0 scale-95' : 'opacity-100 scale-100 font-bold'
          }`}>
            <div className={`px-4 py-2 rounded-full border border-slate-800 shadow-2xl flex items-center space-x-2 text-[10px] font-black uppercase tracking-widest bg-slate-950/95 backdrop-blur-md transition-all duration-500 ${
              hudState === 'complete' 
                ? 'text-emerald-400 shadow-green-500/20' 
                : 'text-cyan-400 shadow-cyan-500/20'
            }`}>
              <span className={`w-2 h-2 rounded-full ${
                hudState === 'complete' ? 'bg-emerald-400 animate-pulse' : 'bg-cyan-400 animate-ping'
              }`} />
              <span>
                {hudState === 'complete' ? 'COMPLETE' : `LOADING ${chunkProgress}%`}
              </span>
            </div>
          </div>
        )}
        <Helmet>
          <title>{seoMeta.title}</title>
          <meta name="description" content={seoMeta.description} />
          <link rel="canonical" href={canonicalUrl} />
          <meta property="og:title" content={seoMeta.title} />
          <meta property="og:description" content={seoMeta.description} />
          <meta property="og:image" content={product?.thumbnailUrl} />
          {/* Symmetrical Window Scrollbar Eraser: */}
          {/* Forcefully erases any vertical window scrollbar gutters to keep the desktop dashboard 100% viewport locked! */}
          <style>{`
            html, body, #root {
              overflow: hidden !important;
              height: 100vh !important;
              height: 100dvh !important;
              margin: 0 !important;
              padding: 0 !important;
            }
          `}</style>
        </Helmet>
        
        {!isMobile && (
          <>
            <ModelInfoPanel
              model={product}
              productSellers={productSellers}
              onAddParentToCart={handleAddParentProductToCart}
              isVisible={showModelInfo}
              onToggle={() => setShowModelInfo(!showModelInfo)}
              components={enrichedComponents}
              selectedComponent={selectedComponent}
              zRayComponentIds={zRayComponentIds}
              viewMode={viewMode}
              onToggleZRay={handleToggleZRay}
              onComponentSelect={handleComponentSelect}
              onComponentDoubleClick={handleComponentDoubleClick}
              onVisibilityChange={handleVisibilityChange}
              componentVisibility={componentVisibility}
              userId={user?.id}
              isLoggedIn={!!user}
              onAuthRequired={() => setShowAuthModal(true)}
              mode={mode}
              onLogRepair={handleLogRepairTrigger}
              componentWraps={showBeforeWrap ? componentWrapsBefore : componentWraps}
              onUpdateComponentWrap={handleUpdateComponentWrap}
              onRemoveComponentWrap={handleRemoveComponentWrap}
              onSaveWrapConfig={handleSaveWrapConfig}
              isMultiSelectActive={isMultiSelectActive}
              onToggleMultiSelect={handleToggleMultiSelect}
              selectedComponentIds={selectedComponentIds}
              onRemoveSelectedId={handleRemoveSelectedId}
              showBeforeWrap={showBeforeWrap}
              onToggleShowBeforeWrap={setShowBeforeWrap}
              wrapName={wrapName}
              setWrapName={setWrapName}
              wrapNotes={wrapNotes}
              setWrapNotes={setWrapNotes}
              gizmoMode={gizmoMode}
              onGizmoModeChange={setGizmoMode}
              gizmoSize={gizmoSize} // Symmetrical: Pass active gizmo size scale factor down!
              onGizmoSizeChange={setGizmoSize} // Symmetrical: Pass state updater down!
              storeSwapConfigs={storeSwapConfigs}
              sellerComponents={sellerComponents}
              configId={configId} // Symmetrical: Pass active Swap Config ID down!
              onOpenUnspecifiedPartRequest={() => {
                setRequestPartName('');
                setIsPartRequestModalOpen(true);
              }}
              onAddComponentToCart={async (option) => {
                if (!user) {
                  setShowAuthModal(true);
                  return;
                }

                if (mode === 'wrap') {
                  if (!assemblyId) {
                    setNotification({ show: true, message: 'No active vehicle asset selected to attach the wrap component to.', type: 'error' });
                    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
                    return;
                  }
                  try {
                    // Query the latest active invoice number for this assembly to add to it!
                    const { data: invoiceRecords } = await supabase
                      .from('assembly_service_records')
                      .select('invoice_number')
                      .eq('assembly_id', assemblyId)
                      .order('created_at', { ascending: false })
                      .limit(1);
                    
                    const invoiceNo = (invoiceRecords && invoiceRecords[0]?.invoice_number) || 'INV-WRAP-001';

                    const { error: insErr } = await supabase
                      .from('assembly_service_records')
                      .insert({
                        assembly_id: assemblyId,
                        invoice_number: invoiceNo,
                        component_id: option.name || option.customName || selectedComponent?.name || 'Selected Component',
                        status: 'repaired', // Set valid check constraint status
                        cost: parseFloat(option.price || 0),
                        notes: `Custom wrap component added: ${option.name || option.customName || 'Selected Component'}`,
                        client_approved: true
                      });

                    if (insErr) throw insErr;
                    
                    setNotification({ show: true, message: 'Component successfully added to customer invoice!', type: 'success' });
                    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
                  } catch (err) {
                    console.error('Error adding wrap component to customer invoice:', err);
                    setNotification({ show: true, message: 'Failed to add wrap component to invoice: ' + err.message, type: 'error' });
                    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
                  }
                  return;
                }

                // If it is an assembly-only product, redirect to add the entire parent product!
                if (product?.sell_product_only) {
                  await handleAddParentProductToCart();
                  return;
                }

                // If it has manufacturing options or no active commercial listings, show configurations modal!
                const is3D = option.is3dManufacturing || option.isCncCapable || option.isSelfManufacturing || option.fulfillmentPartnerId || option.manufacturerId || option.isPainted || option.is_painted || option.is_painted_only || !option.sellerId;
                if (is3D) {
                  let fullOffering = null;
                  let targetId = option.sellerComponentId || option.id;
                  let sellerId = option.sellerId;

                  // Bulletproof self-healing fallback: If targetId or sellerId is missing on the main row click, 
                  // find the first available option that has a valid sellerComponentId and sellerId!
                  if ((!targetId || !sellerId) && option.availableOptions?.length > 0) {
                    const firstOpt = option.availableOptions.find(o => o.sellerComponentId && o.sellerId);
                    if (firstOpt) {
                      targetId = firstOpt.sellerComponentId;
                      sellerId = firstOpt.sellerId;
                    }
                  }

                  if (targetId && sellerId) {
                    try {
                      fullOffering = await componentService.getById(targetId);
                    } catch (e) {
                      console.warn('Failed to load offering by ID, falling back to dummy:', e);
                    }
                  }
                  
                  if (!fullOffering) {
                    // High-fidelity dynamic fallback: Generate a valid custom manufacturing offering 
                    // so the shopper can configure their 3D print even if no direct static seller is mapped!
                    fullOffering = {
                      id: 'mfg-custom',
                      name: option.name || option.customName || 'Selected Component',
                      price: option.price || product?.price || 45.0, // Default fair price
                      is_self_manufacturing: true,
                      component: {
                        id: option.id || option.dbComponentId,
                        name: option.name || option.customName,
                        is_3d_manufacturing: true,
                        mfg_3d_materials: option.mfg3dMaterials || product?.mfg3dMaterials || ['PLA', 'PETG', 'ABS', 'Resin'],
                        mfg_3d_colors: option.mfg3dColors || product?.mfg3dColors || { black: '#111111', white: '#fbfbfb', red: '#ef4444' }
                      }
                    };
                  }

                  setSelectedComponentOffering({
                    ...fullOffering,
                    globalItem: fullOffering.component
                  });
                  setIsOfferingModalOpen(true);
                  return;
                }

                // Symmetrical Fallback: Standard physical part direct add to cart
                try {
                  const targetId = option.sellerComponentId || option.id || option.dbComponentId;
                  console.log('📢 Loading Parts Specification & Logistics details for:', targetId);
                  
                  // Fetch the component offering details
                  let fullOffering = null;
                  if (targetId) {
                    try {
                      fullOffering = await componentService.getById(targetId);
                    } catch (e) {
                      console.warn('Failed to load offering by ID, falling back to dummy:', e);
                    }
                  }
                  
                  if (fullOffering) {
                    setSelectedComponentOffering({
                      ...fullOffering,
                      globalItem: fullOffering.component
                    });
                    setIsOfferingModalOpen(true); // Launch Parts Specification & Logistics Modal with mini viewer!
                  } else {
                    // Symmetrical Fallback: If no direct offering exists, launch integrated sourcing form!
                    const dummyOffering = {
                      id: 'sourcing',
                      isSourcingRequest: true,
                      name: option.name || option.customName || 'Selected Component',
                      component: option,
                      globalItem: option,
                      productId: product?.id || null,
                      productName: product?.name || null,
                      price: null
                    };
                    setSelectedComponentOffering(dummyOffering);
                    setIsOfferingModalOpen(true);
                  }
                } catch (err) {
                  console.warn('⚠️ Error loading parts specifications. Falling back to integrated sourcing form:', err);
                  const dummyOffering = {
                    id: 'sourcing',
                    isSourcingRequest: true,
                    name: option.name || option.customName || 'Selected Component',
                    component: option,
                    globalItem: option,
                    productId: product?.id || null,
                    productName: product?.name || null,
                    price: null
                  };
                  setSelectedComponentOffering(dummyOffering);
                  setIsOfferingModalOpen(true);
                }
              }}
            />
            <ViewerControls
              isFullscreen={isFullscreen}
              onToggleFullscreen={() => setIsFullscreen(p => !p)}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              isPlaying={isPlaying}
              onToggleAnimation={() => setIsPlaying(p => !p)}
              onScreenshot={handleScreenshot}
              onShare={handleShare}
              onReset={handleReset}
              showGrid={showGrid}
              onToggleGrid={() => setShowGrid(p => !p)}
              isInfiniteVertical={isInfiniteVertical}
              onToggleInfiniteVertical={() => setIsInfiniteVertical(p => !p)}
              showLighting={showLighting}
              onToggleLighting={() => setShowLighting(p => !p)}
              materialColor={selectedComponent ? componentColors[selectedComponent.id] : ((product?.sellProductOnly || product?.sell_product_only) ? componentColors['*'] : '#ffffff')}
              onMaterialColorChange={(selectedComponent || product?.sellProductOnly || product?.sell_product_only) ? handleMaterialColorChange : null}
              isExploded={isExploded}
              onToggleExplode={() => setIsExploded(p => !p)}
              explodeScaleX={explodeScaleX}
              explodeScaleY={explodeScaleY}
              explodeScaleZ={explodeScaleZ}
              onExplodeScaleXChange={handleScaleChangeX}
              onExplodeScaleYChange={handleScaleChangeY}
              onExplodeScaleZChange={handleScaleChangeZ}
              isExplodeLocked={isExplodeLocked}
              onToggleExplodeLock={handleToggleExplodeLock}
              isLoggedIn={!!user}
              onAuthRequired={() => setShowAuthModal(true)}
            />
          </>
        )}

        {isMobile && (
          <MobileViewerControls
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            isPlaying={isPlaying}
            onToggleAnimation={() => setIsPlaying(p => !p)}
            isExploded={isExploded}
            onToggleExplode={() => setIsExploded(p => !p)}
            explodeScale={explodeScaleX}
            onExplodeScaleChange={handleScaleChangeX}
            onShare={handleShare}
            bottomClass={(product?.sellProductOnly || product?.sell_product_only) ? "bottom-[72px]" : "bottom-0"}
          />
        )}

        <LightingControls lighting={lighting} onLightingChange={setLighting} isVisible={showLighting} onToggle={() => setShowLighting(p => !p)} />
        <AnnotationPanel
         annotations={annotations}
         enrichedComponents={enrichedComponents}
         onAddAnnotation={handleAddAnnotation}
         onDeleteAnnotation={handleDeleteAnnotation}
         onEditAnnotation={handleEditAnnotation}
         onVoteAnnotation={handleVoteAnnotation}
         onSelectAnnotationDetail={(ann) => {
           setSelectedAnnotationDetail(ann);
           setIsAnnotationDetailModalOpen(true);
         }}
         isVisible={showAnnotations}
         onToggle={() => setShowAnnotations(p => !p)}
         selectedComponent={selectedComponent}
         currentUser={user}
        />
        <MeasurementTools 
          isVisible={showMeasurements} 
          onToggle={() => setShowMeasurements(p => !p)} 
          measurements={measurements}
          onAddMeasurement={(m) => setMeasurements(prev => [...prev, m])}
          onClearMeasurements={() => setMeasurements([])}
          activeTool={activeMeasurementTool}
          setActiveTool={setActiveMeasurementTool}
          selectedMeasurementId={selectedMeasurementId}
          onSelectMeasurement={setSelectedMeasurementId}
          unit={unit}
          setUnit={setUnit}
        />

        {isMobile && (
          <>
            <MobilePartsDrawer
               isOpen={showMobilePartsDrawer}
               onClose={() => setShowMobilePartsDrawer(false)}
               components={enrichedComponents}
               searchTerm={mobileSearchTerm}
               onSearchChange={setMobileSearchTerm}
               selectedComponent={selectedComponent}
               zRayComponentIds={zRayComponentIds}
               viewMode={viewMode}
               onToggleZRay={handleToggleZRay}
               onComponentSelect={handleComponentSelect}
               componentVisibility={componentVisibility}
               onToggleVisibility={handleToggleComponentVisibility}
               onIsolate={handleIsolateComponent}
               onAddComponentToCart={async (option) => {
                  if (!user) {
                    setShowAuthModal(true);
                    return;
                  }

                  if (mode === 'wrap') {
                    if (!assemblyId) {
                      setNotification({ show: true, message: 'No active vehicle asset selected to attach the wrap component to.', type: 'error' });
                      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
                      return;
                    }
                    try {
                      const { data: invoiceRecords } = await supabase
                        .from('assembly_service_records')
                        .select('invoice_number')
                        .eq('assembly_id', assemblyId)
                        .order('created_at', { ascending: false })
                        .limit(1);
                      
                      const invoiceNo = (invoiceRecords && invoiceRecords[0]?.invoice_number) || 'INV-WRAP-001';

                      const { error: insErr } = await supabase
                        .from('assembly_service_records')
                        .insert({
                          assembly_id: assemblyId,
                          invoice_number: invoiceNo,
                          component_id: option.name || option.customName || selectedComponent?.name || 'Selected Component',
                          status: 'repaired',
                          cost: parseFloat(option.price || 0),
                          notes: `Custom wrap component added: ${option.name || option.customName || 'Selected Component'}`,
                          client_approved: true
                        });

                      if (insErr) throw insErr;
                      
                      setNotification({ show: true, message: 'Component successfully added to customer invoice!', type: 'success' });
                      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
                    } catch (err) {
                      console.error('Error adding wrap component to customer invoice:', err);
                      setNotification({ show: true, message: 'Failed to add wrap component to invoice: ' + err.message, type: 'error' });
                      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
                    }
                    return;
                  }

                  // If it is an assembly-only product, redirect to add the entire parent product!
                  if (product?.sell_product_only) {
                    await handleAddParentProductToCart();
                    return;
                  }

                  // If it has manufacturing options or no active commercial listings, show configurations modal!
                  const is3D = option.is3dManufacturing || option.isCncCapable || option.isSelfManufacturing || option.fulfillmentPartnerId || option.manufacturerId || option.isPainted || option.is_painted || option.is_painted_only || !option.sellerId;
                  if (is3D) {
                    let fullOffering = null;
                    let targetId = option.sellerComponentId || option.id;
                    let sellerId = option.sellerId;

                    // Bulletproof self-healing fallback: If targetId or sellerId is missing on the main row click, 
                    // find the first available option that has a valid sellerComponentId and sellerId!
                    if ((!targetId || !sellerId) && option.availableOptions?.length > 0) {
                      const firstOpt = option.availableOptions.find(o => o.sellerComponentId && o.sellerId);
                      if (firstOpt) {
                        targetId = firstOpt.sellerComponentId;
                        sellerId = firstOpt.sellerId;
                      }
                    }

                    if (targetId && sellerId) {
                      try {
                        fullOffering = await componentService.getById(targetId);
                      } catch (e) {
                        console.warn('Failed to load offering by ID, falling back to dummy:', e);
                      }
                    }
                    
                    if (fullOffering) {
                      setSelectedComponentOffering({
                        ...fullOffering,
                        globalItem: fullOffering.component
                      });
                      setIsOfferingModalOpen(true);
                      setShowMobilePartsDrawer(false);
                      return;
                    } else {
                      // Symmetrical Sourcing Fallback!
                      const dummyOffering = {
                        id: 'sourcing',
                        isSourcingRequest: true,
                        name: option.name || option.customName || 'Selected Component',
                        component: option,
                        globalItem: option,
                        productId: product?.id || null,
                        productName: product?.name || null,
                        price: null
                      };
                      setSelectedComponentOffering(dummyOffering);
                      setIsOfferingModalOpen(true);
                      setShowMobilePartsDrawer(false);
                      return;
                    }
                  }

                  try {
                    await cartService.addToCart({
                      userId: user.id,
                      productId: product.id,
                      componentId: option.id || option.dbComponentId,
                      sellerId: option.sellerId,
                      quantity: 1,
                      price: option.price
                    });
                    alert('Added to cart!');
                  } catch (err) {
                    alert(err.message || 'Failed to add to cart');
                  }
               }}
            />
            <MobileSpecsDrawer
              isOpen={showMobileSpecs}
              onClose={() => setShowMobileSpecs(false)}
              model={product}
              onAddParentToCart={handleAddParentProductToCart}
            />
          </>
        )}

        <LoginSignupModal 
          isOpen={showAuthModal} 
          initialMode="login" 
          onClose={() => setShowAuthModal(false)} 
          onSuccess={() => setShowAuthModal(false)} 
          message="Must be logged in with active account to use Screenshot." 
        />

        {!isFullscreen && !isMobile && (
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
            <div className="bg-white/[0.88] backdrop-blur-md rounded-lg border border-border shadow-floating p-2 flex items-center space-x-0.5">
              <Button variant={showModelInfo ? "default" : "ghost"} size="sm" iconName="Info" onClick={() => setShowModelInfo(p => !p)} title="Model Information" />
              <Button variant={showAnnotations ? "default" : "ghost"} size="sm" iconName="MessageSquare" onClick={() => setShowAnnotations(p => !p)} title="Annotations" />
              <Button variant={isRepairModalOpen ? "default" : "ghost"} size="sm" iconName="Wrench" onClick={() => {
                if (!user) {
                  setShowAuthModal(true);
                } else {
                  setIsRepairModalOpen(!isRepairModalOpen);
                }
              }} title="Repair Sequence" />
              <Button variant={showMeasurements ? "default" : "ghost"} size="sm" iconName="Ruler" onClick={() => setShowMeasurements(p => !p)} title="Measurement Tools" />
            </div>
          </div>
        )}

        {isMobile && (product?.sellProductOnly || product?.sell_product_only) && !isFullscreen && (
          <div className="fixed bottom-0 left-0 right-0 p-4 border-t border-border bg-background/95 backdrop-blur-md z-40 pb-safe">
            <Button
              type="button"
              variant="default"
              size="lg"
              className="w-full text-xs font-black uppercase tracking-widest shadow-xl border-b-4 border-primary/50"
              iconName="ShoppingCart"
              onClick={handleAddParentProductToCart}
            >
              Add to Cart
            </Button>
          </div>
        )}
      </main>
      {/* Symmetrical High-Contrast Annotation Detail Modal */}
      {isAnnotationDetailModalOpen && selectedAnnotationDetail && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto animate-in fade-in duration-300">
          <div className="max-w-md w-full rounded-xl bg-white border border-border shadow-2xl overflow-hidden flex flex-col max-h-[85vh] transform transition-all duration-300 scale-100 zoom-in-95">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50/50 shrink-0">
              <div className="flex items-center gap-2">
                <Icon name="MessageSquare" size={18} className="text-primary" />
                <h3 className="text-sm font-bold text-foreground">Annotation Details</h3>
              </div>
              <Button
                variant="ghost"
                size="sm"
                iconName="X"
                onClick={() => {
                  setIsAnnotationDetailModalOpen(false);
                  setSelectedAnnotationDetail(null);
                }}
                className="w-8 h-8 rounded-full"
              />
            </div>
            
            {/* Modal Body */}
            <div className="p-6 space-y-4 overflow-y-auto">
              {/* User Metadata */}
              <div className="flex items-center space-x-3 bg-gray-50/50 p-3 rounded-lg border border-gray-100">
                <div className="w-8 h-8 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center">
                  <Icon name="User" size={14} color="white" />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-800">
                    {(() => {
                      const fName = selectedAnnotationDetail?.user?.firstName || selectedAnnotationDetail?.user?.first_name || '';
                      const lName = selectedAnnotationDetail?.user?.lastName || selectedAnnotationDetail?.user?.last_name || '';
                      if (fName) {
                        return `${fName}${lName ? ` ${lName[0].toUpperCase()}.` : ''}`;
                      }
                      const email = selectedAnnotationDetail?.user?.email || '';
                      if (email) {
                        const local = email.split('@')[0];
                        const parts = local.split(/[\._-]/);
                        const first = parts[0] ? parts[0][0].toUpperCase() : 'User';
                        const last = parts[1] ? ` ${parts[1][0].toUpperCase()}.` : '';
                        return `${first}${last}`;
                      }
                      return 'User';
                    })()}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {selectedAnnotationDetail?.createdAt || selectedAnnotationDetail?.created_at
                      ? new Intl.DateTimeFormat('en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        }).format(new Date(selectedAnnotationDetail.createdAt || selectedAnnotationDetail.created_at))
                      : ''}
                  </p>
                </div>
              </div>

              {/* Part Association Tag */}
              {selectedAnnotationDetail?.component && (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50/50 text-blue-800 rounded-lg border border-blue-100 text-xs font-bold">
                  <Icon name="MapPin" size={14} className="shrink-0 text-blue-600" />
                  <span>Assigned to Part: {selectedAnnotationDetail.component.name || selectedAnnotationDetail.component.customName}</span>
                </div>
              )}

              {/* Full Annotation Comment */}
              <div className="bg-slate-50 border border-slate-100 p-4 rounded-lg min-h-[120px] shadow-inner flex flex-col justify-between">
                <p className="text-xs text-gray-800 leading-relaxed break-words font-medium whitespace-pre-wrap font-sans">
                  {selectedAnnotationDetail.text}
                </p>
                
                {/* Helpfulness Voting Controls inside Modal */}
                <div className="flex items-center gap-3 mt-4 pt-3 border-t border-slate-200/60 text-xs text-gray-500">
                  <span className="font-semibold text-gray-600">Was this annotation helpful?</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleVoteAnnotation(selectedAnnotationDetail.id, 'up')}
                      className="flex items-center gap-1.5 px-2 py-1 bg-white border border-gray-200 rounded-lg hover:bg-green-50 hover:text-green-600 hover:border-green-200 transition cursor-pointer text-xs"
                      title="Helpful"
                    >
                      <Icon name="ThumbsUp" size={12} />
                      <span>{selectedAnnotationDetail.thumbsUp || selectedAnnotationDetail.thumbs_up || 0}</span>
                    </button>
                    
                    <button
                      onClick={() => handleVoteAnnotation(selectedAnnotationDetail.id, 'down')}
                      className="flex items-center gap-1.5 px-2 py-1 bg-white border border-gray-200 rounded-lg hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition cursor-pointer text-xs"
                      title="Not Helpful"
                    >
                      <Icon name="ThumbsDown" size={12} />
                      <span>{selectedAnnotationDetail.thumbsDown || selectedAnnotationDetail.thumbs_down || 0}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 bg-gray-50/30 border-t border-gray-100 flex justify-end shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsAnnotationDetailModalOpen(false);
                  setSelectedAnnotationDetail(null);
                }}
                className="px-5 py-1.5 text-xs font-bold cursor-pointer"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Symmetrical Enterprise Repair Sequences Sidebars */}
      {isRepairModalOpen && !repairBuilderActive && (
        <div className="absolute top-[120px] left-4 z-30 w-[330px] h-[calc(100vh-140px)] bg-slate-950 rounded-lg border border-slate-800 shadow-floating flex flex-col transition-all duration-200">
          <RepairGuidePanel
            guides={repairGuides}
            activeStepIndex={activeStepIndex}
            onStepSelect={handleStepSelect}
            currentGuideId={activeGuideId}
            onGuideSelect={(id) => {
              setActiveGuideId(id);
              setActiveStepIndex(0);
            }}
            onStartAuthoring={(isEdit) => {
              if (isEdit) {
                setRepairBuilderActive(true);
              } else {
                // Clear active guide selection to start fresh
                setActiveGuideId('');
                setRepairBuilderActive(true);
              }
            }}
            onClose={handleCloseRepairPanel}
            onSelectPart={(partName) => {
              const matched = enrichedComponents.find(c => c.name?.toLowerCase() === partName.toLowerCase());
              if (matched) {
                setSelectedComponent(matched);
              } else {
                setSelectedComponent({ id: partName, name: partName });
              }
            }}
            selectedComponent={selectedComponent}
            showStringLines={showStringLines}
            onToggleStringLines={() => setShowStringLines(!showStringLines)}
            onSelectCameraAngle={(ang) => {
              window.dispatchEvent(new CustomEvent('apply-step-camera', { detail: ang }));
            }}
          />
        </div>
      )}

      {isRepairModalOpen && repairBuilderActive && (
        <div className="absolute top-[120px] left-4 z-30 w-[330px] h-[calc(100vh-140px)] bg-slate-950 rounded-lg border border-slate-800 shadow-floating flex flex-col transition-all duration-200">
          <RepairGuideBuilderPanel
            productId={modelId}
            componentId={initialComponentId}
            initialGuide={activeGuideId ? repairGuides.find(g => g.id === activeGuideId) : null}
            selectedPartName={selectedComponent?.name}
            activeStepIndex={activeStepIndex}
            onStepIndexChange={setActiveStepIndex}
            activeStepActions={activeStepActions}
            onUpdateStepActions={(actions, cumulative) => {
              setActiveStepActions(actions);
              setRepairGuideOffsets(cumulative);
            }}
            onSave={async (guideData, steps) => {
              try {
                const savedId = await repairGuideService.saveFullGuide(guideData, steps, user?.id);
                alert('Repair Guide saved successfully!');
                setRepairBuilderActive(false);
                await loadRepairGuides();
                setActiveGuideId(savedId);
                setActiveStepIndex(0);
              } catch (err) {
                console.error('Error saving repair guide:', err);
                alert('Error saving repair guide: ' + err.message);
              }
            }}
            onClose={handleCloseRepairPanel}
            onSelectPart={(partName) => {
              const matched = enrichedComponents.find(c => c.name?.toLowerCase() === partName.toLowerCase());
              if (matched) {
                setSelectedComponent(matched);
              } else {
                setSelectedComponent({ id: partName, name: partName });
              }
            }}
            gizmoMode={repairGizmoMode}
            onGizmoModeChange={setRepairGizmoMode}
          />
        </div>
      )}
      <SellerOfferingDetailModal
        offering={selectedComponentOffering}
        isOpen={isOfferingModalOpen}
        isComponent={true}
        sellersList={doubleClickedSellers}
        parentModelUrl={product?.modelUrl}
        componentWraps={componentWraps}
        onClose={() => {
            setIsOfferingModalOpen(false);
            setSelectedComponentOffering(null);
            setDoubleClickedSellers([]);
            setSelectedComponent(null); // Symmetrically release the selection highlight and focus on close!
            setPartDetailComponent(null);
        }}
        onAddToCart={async (item) => {
            try {
                await cartService.addToCart({
                    userId: user.id,
                    productId: product.id,
                    componentId: item.componentId || item.globalComponentId || item.id,
                    sellerComponentId: item.sellerComponentId,
                    sellerId: item.sellerId,
                    quantity: item.metadata?.quantity || 1,
                    price: item.price,
                    metadata: item.metadata
                });
                
                setIsOfferingModalOpen(false);
                setSelectedComponentOffering(null);
                setDoubleClickedSellers([]);
                setPartDetailComponent(null);
                setNotification({ show: true, message: 'Manufacturing service added to cart!', type: 'success' });
                setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
            } catch (err) {
                setNotification({ show: true, message: err.message || 'Failed to add to cart', type: 'error' });
                setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
            }
        }}
        isAuthenticated={!!user}
        onAuthRequired={() => setShowAuthModal(true)}
        isComponent={true}
        hideViewIn3D={true}
        isAuthModalOpen={showAuthModal}
      />

      {/* Symmetrical Component Detail Modal with Mini-3D Mesh and Sourcing Request Fallback */}
      {isPartDetailModalOpen && partDetailComponent && (
        <div className="fixed inset-0 z-[100] p-4 bg-black/60 backdrop-blur-sm overflow-y-auto animate-in fade-in duration-200">
          <div className="relative bg-card border border-border w-full max-w-4xl rounded-2xl shadow-floating overflow-hidden flex flex-col mx-auto mt-[160px] mb-6 md:mt-[100px] md:mb-12 max-h-none md:max-h-[90vh]">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-border bg-muted/30">
              <div className="max-w-[85%]">
                <h3 className="text-lg font-black text-foreground uppercase tracking-wider leading-tight">
                  Part Specifications & Logistics
                </h3>
                <p className="text-xs text-muted-foreground uppercase font-bold mt-0.5">
                  GLOBAL ID: {partDetailComponent.name}
                </p>
              </div>
              <button 
                onClick={() => {
                  setIsPartDetailModalOpen(false);
                  setPartDetailComponent(null);
                }}
                className="p-2 hover:bg-muted rounded-full transition-colors text-muted-foreground hover:text-foreground flex-shrink-0"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            {/* Modal Body Container */}
            <div className="flex-1 overflow-y-auto p-6 grid grid-cols-1 md:grid-cols-2 gap-8 max-w-full overflow-x-hidden">
              
              {/* Left Column: Interactive 3D Mini-Mesh or Fallback Thumbnail */}
              <div className="flex flex-col space-y-4 max-w-full overflow-hidden">
                <div className="w-full aspect-square bg-gradient-to-br from-background to-muted/20 border border-border rounded-xl overflow-hidden shadow-inner relative flex items-center justify-center max-w-full">
                  {(() => {
                    const queryParams = product?.modelUrl?.includes('?') ? product.modelUrl.substring(product.modelUrl.indexOf('?')) : '';
                    const baseFolder = product?.modelUrl?.substring(0, product.modelUrl.lastIndexOf('/') + 1) || '';
                    const chunkUrl = partDetailComponent.chunkName ? `${baseFolder}${partDetailComponent.chunkName}${queryParams}` : null;

                    if (chunkUrl) {
                      return (
                        <Suspense fallback={
                          <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm z-10 rounded-xl">
                            <span className="text-cyan-600 text-xs font-bold uppercase tracking-wider animate-pulse flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-cyan-500 animate-ping" />
                              Loading Part Mesh...
                            </span>
                          </div>
                        }>
                          <Canvas camera={{ position: [1.8, 1.8, 1.8], fov: 40 }} shadows className="w-full h-full">
                            <ambientLight intensity={0.4} />
                            <directionalLight position={[5, 5, 5]} intensity={1} castShadow />
                            <MiniMeshViewer chunkUrl={chunkUrl} partName={partDetailComponent.name} />
                            <OrbitControls enableDamping dampingFactor={0.05} />
                            <Environment preset="studio" />
                          </Canvas>
                        </Suspense>
                      );
                    }

                    // Fallback to static thumbnail image
                    return (
                      <img
                        src={partDetailComponent.thumbnailPath || product?.thumbnailUrl || "/assets/images/no_image.png"}
                        alt={partDetailComponent.name}
                        className="w-full h-full object-contain p-4 max-w-full rounded-xl transition hover:scale-105"
                      />
                    );
                  })()}
                </div>
                <div className="text-center">
                  <span className="text-[10px] text-muted-foreground uppercase font-black tracking-widest bg-muted px-3 py-1.5 rounded-full">
                    {partDetailComponent.chunkName ? '3D Part' : 'Part Reference'}
                  </span>
                </div>
              </div>

              {/* Right Column: Part Details & Logistics / Sourcing request */}
              <div className="flex flex-col justify-between max-w-full overflow-hidden">
                <div className="space-y-6 max-w-full">
                  {/* Details Block */}
                  <div className="max-w-full">
                    <span className="px-2.5 py-1 bg-primary/10 text-primary text-[10px] font-black rounded-full uppercase tracking-wider block w-max">
                      Category: {partDetailComponent.group || 'Component'}
                    </span>
                    <p className="text-base font-black text-primary font-mono mt-4 break-all uppercase tracking-widest">
                      SKU ID: {partDetailComponent.dbComponentId || partDetailComponent.id}
                    </p>
                  </div>

                  {/* Sellers List / Sourcing request Form */}
                  {isPartSellersLoading ? (
                    <div className="flex flex-col items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4" />
                      <p className="text-xs text-muted-foreground font-semibold uppercase tracking-widest animate-pulse">Scanning sellers...</p>
                    </div>
                  ) : partSellers.length > 0 ? (
                    <div className="space-y-4 max-w-full">
                      <h5 className="text-xs font-black uppercase tracking-wider text-muted-foreground">
                        Available Seller Offerings
                      </h5>
                      <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1 max-w-full">
                        {partSellers.map((sellerOffering) => {
                          const stockQty = sellerOffering.stock_quantity ?? sellerOffering.stockQuantity ?? 0;
                          const is3D = 
                            !!sellerOffering.is_self_manufacturing || 
                            !!sellerOffering.isSelfManufacturing || 
                            !!sellerOffering.manufacturer_id || 
                            !!sellerOffering.manufacturerId ||
                            !!sellerOffering.is_painted ||
                            !!sellerOffering.isPainted ||
                            !!sellerOffering.is_painted_only;
                          const isOutOfStock = stockQty <= 0 && !is3D;

                          return (
                            <div key={sellerOffering.id} className="p-4 border border-border bg-muted/20 rounded-xl flex items-center justify-between hover:border-primary/40 transition-colors max-w-full">
                              <div className="max-w-[60%]">
                                <p className="text-sm font-bold text-foreground truncate" title={sellerOffering.custom_name || sellerOffering.customName || partDetailComponent?.name}>
                                  {sellerOffering.custom_name || sellerOffering.customName || partDetailComponent?.name || 'Component Part'}
                                </p>
                                <p className="text-xs text-muted-foreground truncate mt-0.5" title={sellerOffering.seller?.business_name}>
                                  Sold by: {sellerOffering.seller?.business_name || 'Direct Seller'}
                                </p>
                                <p className={`text-[11px] font-semibold mt-1 ${isOutOfStock ? 'text-red-500' : 'text-success'}`}>
                                  {isOutOfStock ? 'Out of Stock' : `In Stock (${stockQty})`}
                                </p>
                              </div>
                              <div className="flex items-center gap-4 flex-shrink-0">
                                <span className="text-base font-black text-primary">
                                  {formatPrice(sellerOffering.price)}
                                </span>
                                <Button
                                  variant="default"
                                  size="sm"
                                  iconName="ShoppingCart"
                                  disabled={isOutOfStock}
                                  onClick={async () => {
                                    if (!user) {
                                      setShowAuthModal(true);
                                      return;
                                    }

                                    if (is3D) {
                                      // Symmetrical 3D Manufacturing config modal routing!
                                      // Pre-fetches full detailed options (material, infill, colors)
                                      // and mounts the SellerOfferingDetailModal configuration dialog!
                                      try {
                                        const fullOffering = await componentService.getById(sellerOffering.id);
                                        setSelectedComponentOffering({
                                          ...fullOffering,
                                          globalItem: fullOffering.component
                                        });
                                        setIsOfferingModalOpen(true);
                                        setIsPartDetailModalOpen(false); // Close the selector modal seamlessly
                                      } catch (err) {
                                        console.error('Error opening 3D manufacturing dialog:', err);
                                      }
                                      return;
                                    }

                                    try {
                                      await cartService.addToCart({
                                        userId: user.id,
                                        productId: product.id,
                                        componentId: partDetailComponent.dbComponentId || partDetailComponent.id,
                                        sellerComponentId: sellerOffering.id,
                                        sellerId: sellerOffering.seller?.id || sellerOffering.seller_id,
                                        quantity: 1,
                                        price: sellerOffering.price
                                      });
                                      setNotification({ show: true, message: 'Component added to cart!', type: 'success' });
                                      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
                                      setIsPartDetailModalOpen(false);
                                    } catch (err) {
                                      alert(err.message || 'Failed to add to cart');
                                    }
                                  }}
                                >
                                  Add
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    /* Sourcing request fallback form */
                    <div className="p-5 border border-amber-500/20 bg-amber-500/5 rounded-2xl max-w-full">
                      <h5 className="text-xs font-black uppercase tracking-wider text-amber-600 flex items-center gap-1.5 mb-2 leading-tight">
                        <Icon name="Zap" size={14} className="fill-current text-amber-500" />
                        NO ACTIVE SELLERS - SUBMIT PART REQUEST
                      </h5>
                      <p className="text-xs text-muted-foreground leading-relaxed mb-1 whitespace-normal break-words">
                        This part has no direct seller inventory listed. You can submit a part request to our manufacture-capable suppliers instantly.
                      </p>
                      <p className="text-[11px] text-muted-foreground/80 italic leading-relaxed mb-4 whitespace-normal break-words">
                        Submitting a request is completely free and conducts a search across our network of certified sellers to locate this specialty part.
                      </p>
                      
                      {sourcingSuccessMessage ? (
                        <div className="p-3 bg-green-500/10 border border-green-500/20 text-green-500 text-xs font-semibold rounded-lg whitespace-normal break-words">
                          {sourcingSuccessMessage}
                        </div>
                      ) : (
                        <form onSubmit={handleSourcingRequestSubmit} className="space-y-4 max-w-full">
                          <div>
                            <label className="block text-[10px] uppercase font-black tracking-widest text-muted-foreground mb-1.5">
                              Quantity Needed
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={sourcingQuantity}
                              onChange={(e) => setSourcingQuantity(parseInt(e.target.value) || 1)}
                              className="w-full h-9 rounded-md border border-border bg-muted/40 hover:bg-muted/60 focus:bg-background px-3 py-1 text-sm shadow-sm transition-all text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] uppercase font-black tracking-widest text-muted-foreground mb-1.5">
                              Needed By Date (Optional)
                            </label>
                            <input
                              type="date"
                              value={neededByDate}
                              onChange={(e) => setNeededByDate(e.target.value)}
                              className="w-full h-9 rounded-md border border-border bg-muted/40 hover:bg-muted/60 focus:bg-background px-3 py-1 text-sm shadow-sm transition-all text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] uppercase font-black tracking-widest text-muted-foreground mb-1.5">
                              Sourcing Notes / Materials
                            </label>
                            <textarea
                              rows="3"
                              value={sourcingNotes}
                              onChange={(e) => setSourcingNotes(e.target.value)}
                              placeholder="Describe specific material grades, CNC/3D tolerances, or custom coating instructions..."
                              className="w-full rounded-md border border-border bg-muted/40 hover:bg-muted/60 focus:bg-background px-3 py-2 text-sm shadow-sm transition-all text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary text-xs placeholder:text-muted-foreground/60 outline-none"
                            />
                          </div>

                          {/* Optional Photo Attachment Section */}
                          <div>
                            <label className="block text-[10px] uppercase font-black tracking-widest text-muted-foreground mb-1.5">
                              Attach Optional Photo (PNG/JPG)
                            </label>
                            <div className="flex items-center gap-3">
                              <label className="flex items-center gap-2 px-3 py-2 bg-muted/40 hover:bg-muted/60 text-xs font-bold rounded-lg border border-border cursor-pointer transition-colors text-foreground">
                                <Icon name="Upload" size={14} className="text-muted-foreground" />
                                {partPhoto ? partPhoto.name : 'Choose File'}
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      setPartPhoto(file);
                                      setPartPhotoPreview(URL.createObjectURL(file));
                                    }
                                  }}
                                  className="hidden"
                                />
                              </label>
                              {partPhoto && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setPartPhoto(null);
                                    setPartPhotoPreview('');
                                  }}
                                  className="text-xs text-red-500 hover:underline font-semibold"
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                            {partPhotoPreview && (
                              <div className="mt-3 relative w-16 h-16 rounded-lg overflow-hidden border border-border">
                                <img src={partPhotoPreview} alt="Preview" className="w-full h-full object-cover" />
                              </div>
                            )}
                          </div>

                          <div className="flex items-center gap-2 pt-1">
                            <input
                              type="checkbox"
                              id="emailNotification"
                              checked={emailNotification}
                              onChange={(e) => setEmailNotification(e.target.checked)}
                              className="h-4 w-4 rounded border-border text-primary focus:ring-primary bg-muted/40"
                            />
                            <label htmlFor="emailNotification" className="text-xs text-muted-foreground select-none cursor-pointer">
                              Notify me via email when sellers are located
                            </label>
                          </div>

                          <Button
                            type="submit"
                            variant="default"
                            disabled={isSubmittingSourcing}
                            className="w-full uppercase font-black tracking-widest text-xs h-10 shadow-lg shadow-primary/20"
                          >
                            {isSubmittingSourcing ? 'Submitting...' : 'Submit Part Request'}
                          </Button>
                        </form>
                      )}
                    </div>
                  )}
                </div>
              </div>

            </div>

          </div>
        </div>
      )}

      {showSellersModal && (
        <SellerListModal
          isOpen={showSellersModal}
          onClose={() => setShowSellersModal(false)}
          item={product}
          isComponent={false}
          onAddToCart={async (cartItem) => {
            if (!user) {
              setShowAuthModal(true);
              return;
            }

            const isMfg = product.is3dManufacturing || product.isCncCapable || product.isPainted || product.is_painted;
            if (isMfg) {
              setShowSellersModal(false);
              try {
                // Query complete details for this specific seller offering
                const { data: sellerOffering } = await supabase
                  .from('seller_products')
                  .select(`
                    id, price, stock_amount, purchase_limit, vin_sn, is_self_manufacturing, manufacturer_id,
                    seller:seller_accounts(id, business_name, business_slug, rating)
                  `)
                  .eq('id', cartItem.sellerProductId)
                  .maybeSingle();

                if (sellerOffering) {
                  const { data: mfgOpt } = await supabase
                    .from('manufacturing_options')
                    .select('*')
                    .eq('seller_product_id', sellerOffering.id)
                    .maybeSingle();

                  const fullProductOffering = {
                    id: sellerOffering.id,
                    productId: product.id,
                    name: product.name,
                    sku: product.sku,
                    thumbnailPath: product.thumbnailPath || product.thumbnailUrl,
                    is3dManufacturing: product.is3dManufacturing,
                    isCncCapable: product.isCncCapable,
                    isSelfManufacturing: sellerOffering.is_self_manufacturing !== false,
                    manufacturerId: sellerOffering.manufacturer_id || sellerOffering.manufacturerId,
                    manufacturer_id: sellerOffering.manufacturer_id || sellerOffering.manufacturerId,
                    sellerBusinessName: sellerOffering.seller?.business_name,
                    seller: sellerOffering.seller,
                    mfg3dTypes: mfgOpt ? (mfgOpt.mfg_3d_types || []) : (product.mfg3dTypes || []),
                    mfg3dMaterials: mfgOpt ? (mfgOpt.mfg_3d_materials || []) : (product.mfg3dMaterials || []),
                    mfg3dColors: mfgOpt ? (mfgOpt.mfg_3d_colors || {}) : (product.mfg3dColors || {}),
                    mfgCncTypes: mfgOpt ? (mfgOpt.mfg_cnc_types || []) : (product.mfgCncTypes || []),
                    mfgCncMaterials: mfgOpt ? (mfgOpt.mfg_cnc_materials || []) : (product.mfgCncMaterials || []),
                    mfgCncColors: mfgOpt ? (mfgOpt.mfg_cnc_colors || {}) : (product.mfgCncColors || {}),
                    globalItem: product,
                    price: sellerOffering.price
                  };
                  setSelectedOffering(fullProductOffering);
                  setIsProductModalOpen(true);
                  return;
                }
              } catch (err) {
                console.error('Error loading config modal from Sellers modal:', err);
              }
            }

            try {
              const payload = {
                userId: user.id,
                productId: product.id,
                sellerProductId: cartItem.sellerProductId,
                sellerId: cartItem.sellerId,
                quantity: 1,
                price: cartItem.price,
                metadata: { type: 'physical' }
              };
              await cartService.addToCart(payload);
              setNotification({ show: true, message: `Added entire product "${product.name}" to cart!`, type: 'success' });
              setTimeout(() => setNotification({ show: false, message: '', type: '' }), 4000);
              setShowSellersModal(false);
            } catch (err) {
              setNotification({ show: true, message: err.message || 'Failed to add to cart', type: 'error' });
              setTimeout(() => setNotification({ show: false, message: '', type: '' }), 4000);
            }
          }}
          onViewDetails={async (sellerOffering) => {
            setShowSellersModal(false);
            try {
              // Symmetrically fetch full manufacturing options & join globalItem (product)
              const { data: mfgOpt } = await supabase
                .from('manufacturing_options')
                .select('*')
                .eq('seller_product_id', sellerOffering.id)
                .maybeSingle();

              const fullProductOffering = {
                id: sellerOffering.id,
                productId: product.id,
                name: product.name,
                sku: product.sku,
                thumbnailPath: product.thumbnailPath || product.thumbnailUrl,
                is3dManufacturing: product.is3dManufacturing,
                isCncCapable: product.isCncCapable,
                isSelfManufacturing: sellerOffering.is_self_manufacturing !== false,
                manufacturerId: sellerOffering.manufacturer_id || sellerOffering.manufacturerId,
                manufacturer_id: sellerOffering.manufacturer_id || sellerOffering.manufacturerId,
                sellerBusinessName: sellerOffering.seller?.business_name,
                seller: sellerOffering.seller,
                mfg3dTypes: mfgOpt ? (mfgOpt.mfg_3d_types || []) : (product.mfg3dTypes || []),
                mfg3dMaterials: mfgOpt ? (mfgOpt.mfg_3d_materials || []) : (product.mfg3dMaterials || []),
                mfg3dColors: mfgOpt ? (mfgOpt.mfg_3d_colors || {}) : (product.mfg3dColors || {}),
                mfgCncTypes: mfgOpt ? (mfgOpt.mfg_cnc_types || []) : (product.mfgCncTypes || []),
                mfgCncMaterials: mfgOpt ? (mfgOpt.mfg_cnc_materials || []) : (product.mfgCncMaterials || []),
                mfgCncColors: mfgOpt ? (mfgOpt.mfg_cnc_colors || {}) : (product.mfgCncColors || {}),
                globalItem: product,
                price: sellerOffering.price
              };
              setSelectedOffering(fullProductOffering);
              setIsProductModalOpen(true);
            } catch (err) {
              console.error('Error loading full offering detail:', err);
              setSelectedOffering(sellerOffering); // Fallback
              setIsProductModalOpen(true);
            }
          }}
          isAuthenticated={!!user}
          onAuthRequired={() => setShowAuthModal(true)}
        />
      )}

      {selectedOffering && isProductModalOpen && (
        <SellerOfferingDetailModal
          isOpen={isProductModalOpen}
          offering={selectedOffering}
          isComponent={false}
          onClose={() => setIsProductModalOpen(false)}
          onAddToCart={async (cartItem) => {
            if (!user) {
              setShowAuthModal(true);
              return;
            }
            try {
              const payload = {
                userId: user.id,
                productId: product.id,
                sellerProductId: cartItem.sellerProductId || selectedOffering.id,
                sellerId: cartItem.sellerId,
                quantity: 1,
                price: cartItem.price,
                metadata: cartItem.metadata
              };
              await cartService.addToCart(payload);
              setNotification({ show: true, message: `Added custom manufactured "${product.name}" to cart!`, type: 'success' });
              setTimeout(() => setNotification({ show: false, message: '', type: '' }), 4000);
              setIsProductModalOpen(false);
            } catch (err) {
              setNotification({ show: true, message: err.message || 'Failed to add custom manufactured item to cart', type: 'error' });
              setTimeout(() => setNotification({ show: false, message: '', type: '' }), 4000);
            }
          }}
          isAuthenticated={!!user}
          onAuthRequired={() => setShowAuthModal(true)}
        />
      )}

      {/* Notification Toast */}
      {notification.show && (
        <div className={`fixed bottom-4 right-4 z-[150] px-6 py-3 rounded-lg shadow-2xl text-white transform transition-all duration-300 animate-in slide-in-from-bottom-5 ${
          notification.type === 'error' ? 'bg-red-600' : 'bg-green-600'
        }`}>
          <div className="flex items-center gap-3">
            <Icon name={notification.type === 'error' ? 'AlertCircle' : 'Check'} size={20} />
            <span className="font-bold text-sm">{notification.message}</span>
            <button onClick={() => setNotification({ ...notification, show: false })} className="ml-4 hover:opacity-80">
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Symmetrical B2B Repair Shop Service Logger Modal */}
      {showLogRepairModal && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-card border border-border shadow-2xl rounded-2xl w-full max-w-md p-6 relative animate-in fade-in zoom-in-95 duration-200 text-left">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-1.5">
              <Icon name="Plus" className="text-primary" />
              Log Repair Entry
            </h3>
            <form onSubmit={handleSaveRepairLog} className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase text-muted-foreground mb-1">Component Name</label>
                <Input type="text" readOnly value={selectedComponent?.name || ''} className="bg-muted/30" />
              </div>
              
              <div>
                <label className="block text-xs font-bold uppercase text-muted-foreground mb-1">Service Status *</label>
                <select 
                  value={repairForm.status} 
                  onChange={(e) => setRepairForm(p => ({ ...p, status: e.target.value }))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring outline-none"
                  required
                >
                  <option value="replaced">Replaced</option>
                  <option value="repaired">Repaired</option>
                  <option value="needs_repair">Needs Repair</option>
                  <option value="pending_approval">Pending Customer Approval</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-muted-foreground mb-1">Service Cost ($)</label>
                <Input type="number" step="0.01" value={repairForm.cost} onChange={(e) => setRepairForm(p => ({ ...p, cost: e.target.value }))} />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-muted-foreground mb-1">Repair/Replacement Details</label>
                <textarea 
                  value={repairForm.notes} 
                  onChange={(e) => setRepairForm(p => ({ ...p, notes: e.target.value }))}
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-slate-500 mb-1">🔔 Service Resolution (Actual fix completed)</label>
                <textarea 
                  value={repairForm.resolution} 
                  onChange={(e) => setRepairForm(p => ({ ...p, resolution: e.target.value }))}
                  placeholder="Describe the final solution (e.g. tightened primary bracket, aligned steering gears...)"
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring outline-none"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-border mt-4">
                <Button variant="ghost" size="sm" type="button" onClick={() => setShowLogRepairModal(false)}>Cancel</Button>
                <Button variant="default" size="sm" type="submit">Log Service</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Symmetrical 3D Wrap Configurator Success Modal */}
      {showWrapSuccessModal && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-950 border border-slate-800 shadow-2xl rounded-2xl w-full max-w-sm p-6 relative text-center z-10 animate-in fade-in zoom-in-95 duration-200 text-slate-100">
            <button 
              onClick={() => setShowWrapSuccessModal(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors animate-pulse"
            >
              <Icon name="X" size={18} />
            </button>

            <div className="w-12 h-12 bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 rounded-xl flex items-center justify-center mx-auto mb-4 animate-bounce">
              <Icon name="CheckCircle" size={24} />
            </div>

            <h3 className="text-base font-black uppercase tracking-wider mb-2 text-slate-200">
              Design Saved Successfully!
            </h3>
            <p className="text-xs text-slate-400 leading-relaxed mb-6">
              Custom wrap design saved as <span className="font-extrabold text-indigo-400">"{savedProjectName}"</span> and registered in your shop repository!
            </p>

            <Button
              type="button"
              variant="default"
              onClick={() => {
                setShowWrapSuccessModal(false);
                if (window.opener) {
                  window.close();
                } else {
                  navigate(mode === 'swap' ? '/shop-dashboard?tab=swap' : '/shop-dashboard'); // Symmetrical: Use single-page router navigate to prevent 403 hard page reload locks!
                }
              }}
              className="bg-indigo-600 hover:bg-indigo-500 text-white font-black text-xs uppercase tracking-wider h-11 w-full"
            >
              Got it, Close
            </Button>
          </div>
        </div>
      )}

      {/* 3D Component Right-Click Context Menu Overlay */}
      {contextMenu.show && contextMenu.component && (
        <div 
          className="fixed z-[99999] bg-white/95 backdrop-blur-md border border-gray-200 rounded-xl shadow-2xl p-1.5 w-56 flex flex-col gap-0.5 select-none animate-in fade-in zoom-in-95 duration-100"
          style={{ top: `${contextMenu.y}px`, left: `${contextMenu.x}px` }}
          onClick={(e) => e.stopPropagation()} // Prevent closing on clicking menu itself
        >
          {/* Header (Component Name) */}
          <div className="px-3 py-2 border-b border-gray-100 text-[10px] font-black uppercase tracking-wider text-slate-500 truncate">
            {contextMenu.component.name || contextMenu.component.customName || 'Selected Part'}
          </div>

          {/* Action: Toggle Visibility */}
          <button
            onClick={() => {
              handleToggleComponentVisibility(contextMenu.component.id);
              setContextMenu({ show: false, x: 0, y: 0, component: null });
            }}
            className="flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-gray-700 hover:bg-slate-50 rounded-lg transition cursor-pointer text-left w-full"
          >
            <Icon name={(componentVisibility[contextMenu.component.id] ?? true) ? "EyeOff" : "Eye"} size={14} className="text-gray-400 shrink-0" />
            <span>{(componentVisibility[contextMenu.component.id] ?? true) ? "Hide Component" : "Show Component"}</span>
          </button>

          {/* Action: Isolate Component */}
          <button
            onClick={() => {
              handleIsolateComponent(contextMenu.component.id);
              setContextMenu({ show: false, x: 0, y: 0, component: null });
            }}
            className="flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-gray-700 hover:bg-slate-50 rounded-lg transition cursor-pointer text-left w-full"
          >
            <Icon name="Compass" size={14} className="text-gray-400 shrink-0" />
            <span>Isolate Component</span>
          </button>

          {/* Divider */}
          <div className="border-t border-gray-100 my-1" />

          {/* Action: Add to Cart / Pricing Options */}
          <button
            onClick={() => {
              handleComponentDoubleClick(contextMenu.component);
              setContextMenu({ show: false, x: 0, y: 0, component: null });
            }}
            className="flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-indigo-600 hover:bg-indigo-50 rounded-lg transition cursor-pointer text-left w-full"
          >
            <Icon name="ShoppingCart" size={14} className="text-indigo-500 shrink-0" />
            <span>Add to Cart / Pricing</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ModelViewer;
